import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { parseAnalysisUrlParams } from '../../utils/reportingFilterTranslator';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';
import ReactApexChart from 'react-apexcharts';
import { Info, Funnel, LayoutDashboard, TrendingUp, ChevronDown, MousePointerClick, Sparkles } from 'lucide-react';
import { GenericView, SearchInput } from '../../components/common/table';
import { AppSelect } from '../../components/common/select';
import FilterChips from '../../components/FilterChips';
import {
  buildAnalysisPayload,
  buildAnnualRevenueProjectionParams,
  getAnalysis,
  getAnnualRevenueProjectionAnalysis,
  getProjectionYears,
  normalizeAnalysisResponse,
  MONTHS
} from './api/analysisApi';
import api from '../../api';
import { useAuth } from '../../contexts/AuthProvider';
import Loading from '../../components/Loading';
import { buildAnalysisFilterTags } from './analysisUiUtils';
import {
  buildBarChartInsight,
  buildLineChartInsight,
  getInteractiveBarColors,
} from './analysisChartUtils';
import {
  buildMonthlyDrilldownRange,
  findOptionByLabel,
} from './analysisChartFilterUtils';

const InfoTooltip = ({ text }) => (
  <span className="dashboard-info-tooltip-wrapper" data-tooltip={text}>
    <Info
      size={16}
      strokeWidth={2}
      className="dashboard-info-icon"
      style={{ color: '#adb5bd', cursor: 'help', flexShrink: 0 }}
    />
  </span>
);

const formatRupiah = (value) => {
  const parts = Math.floor(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `Rp${parts}`;
};

/** Percentage for display (e.g. win rate), always 2 decimal places */
const formatPercent2 = (value) => {
  const n = Number(value);
  if (value == null || Number.isNaN(n)) return '0.00';
  return n.toFixed(2);
};

const formatIDRShort = (value) => {
  if (value == null || isNaN(value)) return '0';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  let num, suffix;
  if (abs >= 1_000_000_000) {
    num = abs / 1_000_000_000;
    suffix = 'B';
  } else if (abs >= 1_000_000) {
    num = abs / 1_000_000;
    suffix = 'JT';
  } else if (abs >= 1_000) {
    num = abs / 1_000;
    suffix = 'RB';
  } else {
    return `${sign}Rp${Math.round(abs)}`;
  }
  // Remove unnecessary trailing zeros
  const formatted = parseFloat(num.toFixed(2)).toString();
  return `${sign}Rp${formatted}${suffix}`;
};

// Diverse color palette for department charts
const DEPARTMENT_COLORS = [
  '#1f77b4', // blue
  '#ff7f0e', // orange
  '#2ca02c', // green
  '#d62728', // red
  '#9467bd', // purple
  '#8c564b', // brown
  '#e377c2', // pink
  '#7f7f7f', // gray
  '#bcbd22', // olive
  '#17becf', // cyan
  '#aec7e8', // light blue
  '#ffbb78', // light orange
  '#98df8a', // light green
  '#ff9896', // light red
  '#c5b0d5', // light purple
  '#c49c94', // light brown
  '#f7b6d2', // light pink
  '#c7c7c7', // light gray
  '#dbdb8d', // light olive
  '#9edae5'  // light cyan
];

const KPICard = ({ title, value, description }) => (
  <div className="card">
    <div className="card-body position-relative">
      <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex">
        <InfoTooltip text={description} />
      </div>
      <div className="text-uppercase text-muted small mb-1">{title}</div>
      <div className="h2 mb-0">{value}</div>
      <div className="text-muted small">{description}</div>
    </div>
  </div>
);

const totalProposalRowTone = (label) => {
  const t = String(label || '')
    .trim()
    .toLowerCase();
  if (t === 'won' || t.endsWith(' won') || t.startsWith('won ')) return 'success';
  if (t === 'lost' || t.endsWith(' lost') || t.startsWith('lost ')) return 'danger';
  return 'muted';
};

/** Overview KPI: total proposals from API `totalProposalSummary` (see reports-analysis-overview-payload.example.json). */
const TotalProposalSummaryCard = ({ summary, loading, infoTitle }) => {
  const [open, setOpen] = useState(false); // start closed by default
  const rows = summary?.breakdown ?? [];
  // Desired funnel order
  const desiredOrder = [
    'Unknown',
    'Negotiation',
    'Forecast for Closing in the upcoming month (M1)',
    'Forecast for closing in the upcoming 2 months',
    'Forecast for closing in the upcoming 3 months',
    'Won',
    'Lost'
  ];

  const normalize = (s) => (String(s || '').toLowerCase().replace(/\s+/g, ' ').trim());

  // Build ordered rows: try to match labels (case-insensitive, partial) in desired order,
  // then append any remaining rows that weren't matched.
  const used = new Set();
  const orderedRows = [];
  desiredOrder.forEach((label) => {
    const match = rows.find((r, i) => {
      if (used.has(i)) return false;
      const rl = normalize(r.label);
      const nl = normalize(label);
      return rl === nl || rl.includes(nl) || nl.includes(rl);
    });
    if (match) {
      const idx = rows.indexOf(match);
      used.add(idx);
      orderedRows.push(match);
    }
  });
  // Append remaining rows in their original order
  rows.forEach((r, i) => {
    if (!used.has(i)) orderedRows.push(r);
  });
  const total = summary?.total ?? 0;

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="text-uppercase text-muted small mb-0 fw-medium">Total Proposal</div>
          <span className="text-muted d-inline-flex">
            <InfoTooltip text={infoTitle} />
          </span>
        </div>
        <div className="display-6 fw-bold text-dark mb-3" style={{ fontSize: '2.25rem', lineHeight: 1.1 }}>
          {loading ? '—' : total}
        </div>

        {open && (
          <div className="d-flex flex-column gap-2 small">
            {loading && <div className="text-muted">Loading…</div>}
            {!loading &&
              orderedRows.map(({ label, value }, i) => {
                const tone = totalProposalRowTone(label);
                const cls =
                  tone === 'success'
                    ? 'text-success'
                    : tone === 'danger'
                      ? 'text-danger'
                      : 'text-secondary';
                return (
                  <div key={`${label}-${i}`} className={`d-flex justify-content-between align-items-baseline ${cls}`}>
                    <span>{label}:</span>
                    <span className="fw-medium tabular-nums">{value}</span>
                  </div>
                );
              })}
            {!loading && rows.length === 0 && <div className="text-muted">No proposal stage data for this period.</div>}
          </div>
        )}

        {/* Centered dropdown icon button (toggles breakdown). Icon rotates on open */}
        <div className="d-flex justify-content-center mt-3">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? 'Hide details' : 'Show details'}
            title={open ? 'Hide details' : 'Show details'}
            style={{
              width: '100%',
              padding: '0.12rem 0.25rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              height: 28
            }}
          >
            <ChevronDown
              size={16}
              style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.18s ease' }}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

const matchSearch = (row, searchLower, keys) => {
  if (!searchLower.trim()) return true;
  return keys.some((k) => {
    const v = row[k];
    if (v == null) return false;
    if (Array.isArray(v)) return v.some((t) => (t?.label || '').toLowerCase().includes(searchLower));
    return String(v).toLowerCase().includes(searchLower);
  });
};

// Helper function to get diverse colors for departments
const getDepartmentColors = (series) => {
  if (!series || !Array.isArray(series)) return [];
  return series.map((_, index) => DEPARTMENT_COLORS[index % DEPARTMENT_COLORS.length]);
};

const EMPTY_CHART_SELECTIONS = {
  wonValueByDepartment: null,
  lostRateByDepartment: null,
  proposalFunnel: null,
  prospectsByLeadSource: null,
  productWinRate: null,
  sectorsByProposalCount: null,
  lostValueByReason: null,
  lostCountByReason: null,
  annualRevenueProjection: null,
};

const ChartInteractionHint = () => (
  <div className="analysis-chart-hint">
    <MousePointerClick size={14} />
    <span>Click a point or bar to cross-filter every visible card, chart, and table from the backend.</span>
  </div>
);

const LoadingOverlay = ({ show, label = 'Refreshing data...' }) => {
  if (!show) {
    return null;
  }

  return (
    <div className="analysis-loading-overlay" role="status" aria-live="polite">
      <div className="analysis-loading-pill">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        <span>{label}</span>
      </div>
    </div>
  );
};

const FilterAwarenessBar = ({ title, summary, detail, loading }) => (
  <div className="analysis-awareness-bar">
    <div className="analysis-awareness-copy">
      <div className="analysis-awareness-kicker">
        <span className={`analysis-awareness-dot ${loading ? 'is-loading' : ''}`} />
        <span>{loading ? 'Refreshing Analysis' : 'Data Filter'}</span>
      </div>
      <div className="analysis-awareness-title">{title}</div>
      <div className="analysis-awareness-summary">{summary}</div>
      {detail ? <div className="analysis-awareness-detail">{detail}</div> : null}
    </div>
  </div>
);

const ChartInsightPanel = ({ accentClass, kicker, title, summary, stats, onClear }) => {
  if (!title) {
    return null;
  }

  return (
    <div className={`analysis-chart-insight ${accentClass || ''}`}>
      <div className="analysis-chart-insight-header">
        <div>
          <div className="analysis-chart-insight-kicker">
            <Sparkles size={14} />
            <span>{kicker}</span>
          </div>
          <div className="analysis-chart-insight-title">{title}</div>
        </div>
        {onClear ? (
          <button type="button" className="analysis-chart-clear" onClick={onClear} aria-label="Clear pinned insight">
            Clear
          </button>
        ) : null}
      </div>
      <div className="analysis-chart-insight-summary">{summary}</div>
      <div className="analysis-chart-stat-grid">
        {(stats || []).map((stat) => (
          <div className="analysis-chart-stat" key={stat.label}>
            <div className="analysis-chart-stat-label">{stat.label}</div>
            <div className="analysis-chart-stat-value">{stat.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ChartDrilldownBanner = ({ drilldown, onDismiss }) => {
  if (!drilldown) {
    return null;
  }

  return (
    <div className="analysis-chart-drilldown-banner">
      <div className="analysis-chart-drilldown-copy">
        <div className="analysis-chart-drilldown-kicker">Chart Cross-Filter Active</div>
        <div className="analysis-chart-drilldown-title">{drilldown.source}</div>
        <div className="analysis-chart-drilldown-summary">
          {drilldown.selection}
          {Array.isArray(drilldown.appliedFilters) && drilldown.appliedFilters.length > 0
            ? ` • ${drilldown.appliedFilters.join(' • ')}`
            : ''}
        </div>
        {drilldown.note ? <div className="analysis-chart-drilldown-note">{drilldown.note}</div> : null}
      </div>
      <button type="button" className="analysis-chart-dismiss" onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  );
};

const PERIOD_OPTIONS = [
  { id: 'all', name: 'All Time' },
  { id: 'last_3_days', name: 'Last 3 Days' },
  { id: 'last_7_days', name: 'Last 7 Days' },
  { id: 'this_month', name: 'This Month' },
  { id: 'last_month', name: 'Last Month' },
  { id: 'this_year', name: 'This Year' },
  { id: 'last_year', name: 'Last Year' },
  { id: 'custom', name: 'Custom Range' }
];

// Comprehensive ISO 4217 currency list
const CURRENCY_OPTIONS = [
  { code: 'AED', name: 'United Arab Emirates Dirham' },
  { code: 'AFN', name: 'Afghan Afghani' },
  { code: 'ALL', name: 'Albanian Lek' },
  { code: 'AMD', name: 'Armenian Dram' },
  { code: 'ANG', name: 'Netherlands Antillean Guilder' },
  { code: 'AOA', name: 'Angolan Kwanza' },
  { code: 'ARS', name: 'Argentine Peso' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'AWG', name: 'Aruban Florin' },
  { code: 'AZN', name: 'Azerbaijani Manat' },
  { code: 'BAM', name: 'Bosnia-Herzegovina Convertible Mark' },
  { code: 'BBD', name: 'Barbadian Dollar' },
  { code: 'BDT', name: 'Bangladeshi Taka' },
  { code: 'BGN', name: 'Bulgarian Lev' },
  { code: 'BHD', name: 'Bahraini Dinar' },
  { code: 'BIF', name: 'Burundian Franc' },
  { code: 'BMD', name: 'Bermudian Dollar' },
  { code: 'BND', name: 'Brunei Dollar' },
  { code: 'BOB', name: 'Bolivian Boliviano' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'BSD', name: 'Bahamian Dollar' },
  { code: 'BTN', name: 'Bhutanese Ngultrum' },
  { code: 'BWP', name: 'Botswana Pula' },
  { code: 'BYN', name: 'Belarusian Ruble' },
  { code: 'BZD', name: 'Belize Dollar' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'CDF', name: 'Congolese Franc' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'CLP', name: 'Chilean Peso' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'COP', name: 'Colombian Peso' },
  { code: 'CRC', name: 'Costa Rican Colón' },
  { code: 'CUC', name: 'Cuban Convertible Peso' },
  { code: 'CUP', name: 'Cuban Peso' },
  { code: 'CVE', name: 'Cape Verdean Escudo' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'DJF', name: 'Djiboutian Franc' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'DOP', name: 'Dominican Peso' },
  { code: 'DZD', name: 'Algerian Dinar' },
  { code: 'EGP', name: 'Egyptian Pound' },
  { code: 'ERN', name: 'Eritrean Nakfa' },
  { code: 'ETB', name: 'Ethiopian Birr' },
  { code: 'EUR', name: 'Euro' },
  { code: 'FJD', name: 'Fijian Dollar' },
  { code: 'FKP', name: 'Falkland Islands Pound' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'GEL', name: 'Georgian Lari' },
  { code: 'GGP', name: 'Guernsey Pound' },
  { code: 'GHS', name: 'Ghanaian Cedi' },
  { code: 'GIP', name: 'Gibraltar Pound' },
  { code: 'GMD', name: 'Gambian Dalasi' },
  { code: 'GNF', name: 'Guinean Franc' },
  { code: 'GTQ', name: 'Guatemalan Quetzal' },
  { code: 'GYD', name: 'Guyanese Dollar' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'HNL', name: 'Honduran Lempira' },
  { code: 'HRK', name: 'Croatian Kuna' },
  { code: 'HTG', name: 'Haitian Gourde' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'ILS', name: 'Israeli New Shekel' },
  { code: 'IMP', name: 'Isle of Man Pound' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'IQD', name: 'Iraqi Dinar' },
  { code: 'IRR', name: 'Iranian Rial' },
  { code: 'ISK', name: 'Icelandic Króna' },
  { code: 'JEP', name: 'Jersey Pound' },
  { code: 'JMD', name: 'Jamaican Dollar' },
  { code: 'JOD', name: 'Jordanian Dinar' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'KES', name: 'Kenyan Shilling' },
  { code: 'KGS', name: 'Kyrgyzstani Som' },
  { code: 'KHR', name: 'Cambodian Riel' },
  { code: 'KMF', name: 'Comorian Franc' },
  { code: 'KPW', name: 'North Korean Won' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'KWD', name: 'Kuwaiti Dinar' },
  { code: 'KYD', name: 'Cayman Islands Dollar' },
  { code: 'KZT', name: 'Kazakhstani Tenge' },
  { code: 'LAK', name: 'Lao Kip' },
  { code: 'LBP', name: 'Lebanese Pound' },
  { code: 'LKR', name: 'Sri Lankan Rupee' },
  { code: 'LRD', name: 'Liberian Dollar' },
  { code: 'LSL', name: 'Lesotho Loti' },
  { code: 'LYD', name: 'Libyan Dinar' },
  { code: 'MAD', name: 'Moroccan Dirham' },
  { code: 'MDL', name: 'Moldovan Leu' },
  { code: 'MGA', name: 'Malagasy Ariary' },
  { code: 'MKD', name: 'Macedonian Denar' },
  { code: 'MMK', name: 'Burmese Kyat' },
  { code: 'MNT', name: 'Mongolian Tugrik' },
  { code: 'MOP', name: 'Macanese Pataca' },
  { code: 'MRU', name: 'Mauritanian Ouguiya' },
  { code: 'MUR', name: 'Mauritian Rupee' },
  { code: 'MVR', name: 'Maldivian Rufiyaa' },
  { code: 'MWK', name: 'Malawian Kwacha' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'MZN', name: 'Mozambican Metical' },
  { code: 'NAD', name: 'Namibian Dollar' },
  { code: 'NGN', name: 'Nigerian Naira' },
  { code: 'NIO', name: 'Nicaraguan Córdoba' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'NPR', name: 'Nepalese Rupee' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'OMR', name: 'Omani Rial' },
  { code: 'PAB', name: 'Panamanian Balboa' },
  { code: 'PEN', name: 'Peruvian Sol' },
  { code: 'PGK', name: 'Papua New Guinean Kina' },
  { code: 'PHP', name: 'Philippine Peso' },
  { code: 'PKR', name: 'Pakistani Rupee' },
  { code: 'PLN', name: 'Polish Złoty' },
  { code: 'PYG', name: 'Paraguayan Guaraní' },
  { code: 'QAR', name: 'Qatari Riyal' },
  { code: 'RON', name: 'Romanian Leu' },
  { code: 'RSD', name: 'Serbian Dinar' },
  { code: 'RUB', name: 'Russian Ruble' },
  { code: 'RWF', name: 'Rwandan Franc' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'SBD', name: 'Solomon Islands Dollar' },
  { code: 'SCR', name: 'Seychellois Rupee' },
  { code: 'SDG', name: 'Sudanese Pound' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'SHP', name: 'Saint Helena Pound' },
  { code: 'SLL', name: 'Sierra Leonean Leone' },
  { code: 'SOS', name: 'Somali Shilling' },
  { code: 'SRD', name: 'Surinamese Dollar' },
  { code: 'SSP', name: 'South Sudanese Pound' },
  { code: 'STN', name: 'São Tomé and Príncipe Dobra' },
  { code: 'SYP', name: 'Syrian Pound' },
  { code: 'SZL', name: 'Eswatini Lilangeni' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'TJS', name: 'Tajikistani Somoni' },
  { code: 'TMT', name: 'Turkmenistan Manat' },
  { code: 'TND', name: 'Tunisian Dinar' },
  { code: 'TOP', name: "Tongan Paʻanga" },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'TTD', name: 'Trinidad and Tobago Dollar' },
  { code: 'TWD', name: 'New Taiwan Dollar' },
  { code: 'TZS', name: 'Tanzanian Shilling' },
  { code: 'UAH', name: 'Ukrainian Hryvnia' },
  { code: 'UGX', name: 'Ugandan Shilling' },
  { code: 'USD', name: 'United States Dollar' },
  { code: 'UYU', name: 'Uruguayan Peso' },
  { code: 'UZS', name: 'Uzbekistan Som' },
  { code: 'VES', name: 'Venezuelan Bolívar Soberano' },
  { code: 'VND', name: 'Vietnamese Dong' },
  { code: 'VUV', name: 'Vanuatu Vatu' },
  { code: 'WST', name: 'Samoan Tala' },
  { code: 'XAF', name: 'Central African CFA Franc' },
  { code: 'XAG', name: 'Silver (troy ounce)' },
  { code: 'XAU', name: 'Gold (troy ounce)' },
  { code: 'XCD', name: 'East Caribbean Dollar' },
  { code: 'XDR', name: 'IMF Special Drawing Rights' },
  { code: 'XOF', name: 'West African CFA Franc' },
  { code: 'XPF', name: 'CFP Franc' },
  { code: 'YER', name: 'Yemeni Rial' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'ZMW', name: 'Zambian Kwacha' },
  { code: 'ZWL', name: 'Zimbabwean Dollar' }
];

const PROPOSAL_APPROVAL_STATUS_OPTIONS = [
  { id: 'all', name: 'All' },
  { id: 'Draft', name: 'Draft' },
  { id: 'Need Approval', name: 'Need Approval' },
  { id: 'Approved', name: 'Approved' },
  { id: 'Rejected', name: 'Rejected' },
  { id: 'Revision', name: 'Revision' }
];

/**
 * Parse initial Analysis filter state from the current URL search params.
 * Returns both `filters` and `customDateRange` in one pass so state initializers
 * share the same URLSearchParams call instead of parsing twice.
 * Falls back to Analysis page defaults when no URL params are present.
 */
const ANALYSIS_DEFAULTS = {
  filters: {
    period: 'this_year',
    salesCreator: [],
    department: [],
    sector: [],
    leadSource: [],
    standard: [],
    branchId: '',
    taggingIds: [],
    proposalApprovalStatus: '',
    competitor: [],
    lostReason: [],
  },
  customDateRange: { startDate: '', endDate: '' },
};

function initAnalysisStateFromUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  // URL params (drill-down from Dashboard) always take priority
  if (urlParams.size > 0) {
    return parseAnalysisUrlParams(urlParams);
  }
  // Restore from sessionStorage if available
  try {
    const raw = sessionStorage.getItem('list_filter:analysis');
    if (raw) {
      const saved = JSON.parse(raw);
      if (saved?.filters) {
        return {
          filters: { ...ANALYSIS_DEFAULTS.filters, ...saved.filters },
          customDateRange: saved.customDateRange ?? ANALYSIS_DEFAULTS.customDateRange,
        };
      }
    }
  } catch {}
  return ANALYSIS_DEFAULTS;
}

const Analysis = () => {
  const { user, can, capabilitiesLoading } = useAuth();
  const { persistState } = usePersistedFilters('analysis');

  const [activeTab, setActiveTab] = useState('overview');
  // '' means "All currencies" — no filter sent to backend, values shown as IDR.
  // A specific code (e.g. 'USD') sends currencyId to backend and shows raw returned values.
  const [currency, setCurrency] = useState('');
  const [searchQueryLost, setSearchQueryLost] = useState('');
  const [searchQueryProposal, setSearchQueryProposal] = useState('');
  const [proposalCurrentPage, setProposalCurrentPage] = useState(1);
  const [proposalRowsPerPage, setProposalRowsPerPage] = useState(10);
  /** Server-side pagination for Annual Revenue Projection → detailed breakdown */
  const [breakdownApiPage, setBreakdownApiPage] = useState(1);
  const [breakdownApiPageSize, setBreakdownApiPageSize] = useState(10);
  const [arpFetchNonce, setArpFetchNonce] = useState(0);

  const [annualProjectionState, setAnnualProjectionState] = useState(null);
  const [annualProjectionLoading, setAnnualProjectionLoading] = useState(false);
  const [annualProjectionError, setAnnualProjectionError] = useState(null);

  const [proposalStageOptions, setProposalStageOptions] = useState([]);

  // Filter state — initialized from URL query params when present (e.g., drill-down from Dashboard),
  // otherwise falls back to the default Analysis view. Both initializers share a single URL parse.
  const [filters, setFilters] = useState(() => initAnalysisStateFromUrl().filters);
  const [customDateRange, setCustomDateRange] = useState(() => initAnalysisStateFromUrl().customDateRange);
  const [, setChartSelections] = useState(EMPTY_CHART_SELECTIONS);
  const [chartDrilldown, setChartDrilldown] = useState(null);
  const filtersRef = useRef(filters);
  const customDateRangeRef = useRef(customDateRange);
  filtersRef.current = filters;
  customDateRangeRef.current = customDateRange;

  // All HRIS users for department → salesCreator cascade lookup
  const [allAnalysisUsers, setAllAnalysisUsers] = useState([]);
  useEffect(() => {
    api.get('/md/internal-users/option-internal-user')
      .then(res => setAllAnalysisUsers(res.data || []))
      .catch(() => {});
  }, []);

  // Analysis data and filter options from single API: GET /reports-analysis
  const [analysisData, setAnalysisData] = useState(() => normalizeAnalysisResponse(null));
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  /** Prefer reports-analysis `projectionYears` (aligns detailed lost / all-stages year columns with backend). */
  const projectionYears = useMemo(() => {
    const fromAnalysis = analysisData?.projectionYears;
    if (Array.isArray(fromAnalysis) && fromAnalysis.length > 0) {
      return fromAnalysis.map((y) => Number(y));
    }
    const fromArp = annualProjectionState?.annualRevenueProjection?.years;
    if (Array.isArray(fromArp) && fromArp.length > 0) return fromArp.map((y) => Number(y));
    return getProjectionYears();
  }, [analysisData?.projectionYears, annualProjectionState?.annualRevenueProjection?.years]);

  const currentProjectionYear = projectionYears[0];
  // Exchange rates fetched from backend (expects { base: 'IDR', rates: { USD: 0.000067, ... } } or similar)
  const [exchangeRates, setExchangeRates] = useState(null);
  // List of currencies from backend (to map selected currency code -> currencyId)
  const [currenciesList, setCurrenciesList] = useState([]);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState(null);
  // Keep selectedCurrencyId in sync with currency selection and currenciesList
  useEffect(() => {
    if (!Array.isArray(currenciesList) || currenciesList.length === 0) {
      setSelectedCurrencyId(null);
      return;
    }
    const code = (currency || '').toString().trim().toUpperCase();
    if (!code) {
      setSelectedCurrencyId(null);
      return;
    }
    const found = currenciesList.find(c => ((c.currencyCode || '').toString().trim().toUpperCase()) === code || String(c.id) === code);
    setSelectedCurrencyId(found ? found.id : null);
  }, [currency, currenciesList]);
  useEffect(() => {
    const endpoint = process.env.REACT_APP_CURRENCY_RATES_ENDPOINT || '/currency/exchange-rates';
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(endpoint);
        if (!cancelled) setExchangeRates(res.data || null);
      } catch (err) {
        // silent fallback — keep exchangeRates null
        if (!cancelled) setExchangeRates(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Convert a value expressed in IDR (source data) into selected currency using fetched rates.
  const convertFromIDR = (value, toCurrency) => {
    const v = Number(value) || 0;
    // If a currency filter is active (backend returns proposals already in that currency),
    // do not perform client-side conversion — values are already in selected currency.
    if (selectedCurrencyId != null) return v;
    if (!exchangeRates || !exchangeRates.rates || !toCurrency) return v;
    const rates = exchangeRates.rates || {};
    const base = exchangeRates.base || 'IDR';
    // If target is IDR, no conversion
    if (toCurrency === base || toCurrency === 'IDR') return v;
    // Case: API rates are relative to IDR (base === 'IDR'), rates[to] = amount of toCurrency per 1 IDR
    if (base === 'IDR' && rates[toCurrency] != null) {
      return v * Number(rates[toCurrency]);
    }
    // Case: rates include both IDR and target (base != 'IDR'): compute target per IDR as rates[target] / rates['IDR']
    if (rates[toCurrency] != null && rates['IDR'] != null) {
      return v * (Number(rates[toCurrency]) / Number(rates['IDR']));
    }
    // Fallback: if rate looks like IDR per 1 target (large number), treat as IDR per unit -> divide
    if (rates[toCurrency] != null && Number(rates[toCurrency]) > 1) {
      return v / Number(rates[toCurrency]);
    }
    return v;
  };

  const formatCurrencyVal = (value) => {
    // If a currency filter is active (backend returns amounts already in that currency),
    // show the value as-is and format with the selected currency code.
    if (selectedCurrencyId != null) {
      const v = Number(value) || 0;
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency || 'IDR',
          maximumFractionDigits: 2
        }).format(v);
      } catch {
        return `${currency || 'IDR'} ${Math.round(v).toLocaleString()}`;
      }
    }
    const converted = convertFromIDR(value, currency);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'IDR',
        maximumFractionDigits: 2
      }).format(converted);
    } catch {
      return `${currency || 'IDR'} ${Math.round(converted).toLocaleString()}`;
    }
  };

  const formatCurrencyShort = (value) => {
    // If currency filter active, don't convert values — use as-is.
    const conv = selectedCurrencyId != null ? (Number(value) || 0) : convertFromIDR(value, currency);
    if (conv == null || isNaN(conv)) return '0';
    const abs = Math.abs(conv);
    const sign = conv < 0 ? '-' : '';
    let scaled;
    let suffix = '';
    if (abs >= 1e9) {
      scaled = conv / 1e9;
      suffix = 'B';
    } else if (abs >= 1e6) {
      scaled = conv / 1e6;
      suffix = 'M';
    } else if (abs >= 1e3) {
      scaled = conv / 1e3;
      suffix = 'K';
    } else {
      return formatCurrencyVal(conv);
    }
    try {
      const formatted = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, style: 'currency', currency: currency || 'IDR' }).format(scaled);
      return `${formatted}${suffix}`;
    } catch {
      return `${sign}${Math.round(scaled).toLocaleString()}${suffix} ${currency || ''}`;
    }
  };

  // Load currencies list from backend for mapping code -> id
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/crm/currencies');
        if (!cancelled) setCurrenciesList(res.data || []);
      } catch (err) {
        if (!cancelled) setCurrenciesList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadAnalysis = React.useCallback((overrideFilters = null, overrideDateRange = null) => {
    const f = overrideFilters ?? filtersRef.current;
    const d = overrideDateRange ?? customDateRangeRef.current;
    const viewParams = {};
    if (!can('view_global', 'analysis')) {
      if (can('view_department', 'analysis') && user?.departmentId) viewParams.viewDepartmentId = user.departmentId;
      else if (can('view_own', 'analysis') && user?.id) viewParams.viewOwnUserId = user.id;
    }
    // Map selected currency code to backend currencyId (if available)
    try {
      const selectedCode = (currency || '').toString().trim().toUpperCase();
      if (selectedCode) {
        const found = (currenciesList || []).find(c => {
          const code = (c.currencyCode || '').toString().trim().toUpperCase();
          return code === selectedCode;
        });
        if (found && found.id != null) {
          viewParams.currencyId = found.id;
        }
      }
    } catch (e) {
      // ignore mapping errors
    }
    const params = buildAnalysisPayload(f, d, viewParams);
    setIsLoadingAnalysis(true);
    setAnalysisError(null);
    getAnalysis(params)
      .then((data) => {
        setAnalysisData(data);
        setChartSelections(EMPTY_CHART_SELECTIONS);
      })
      .catch((err) => {
        const backendMsg = err?.response?.data?.message;
        const axiosMsg = err?.message;
        const fallback = 'Failed to load analysis. The reports-analysis API may not be implemented yet.';
        const emptyOrGeneric = !backendMsg || /no message available|message not found/i.test(String(backendMsg));
        setAnalysisError(emptyOrGeneric ? (axiosMsg && !/network error/i.test(axiosMsg) ? axiosMsg : fallback) : backendMsg);
        setAnalysisData(normalizeAnalysisResponse(null));
        setChartSelections(EMPTY_CHART_SELECTIONS);
      })
      .finally(() => {
        setIsLoadingAnalysis(false);
      });
  }, [can, user, currency, currenciesList]);

  const dismissChartDrilldown = React.useCallback(() => {
    setChartDrilldown(null);
  }, []);

  const applyFiltersAndReload = React.useCallback((nextFilters, nextDateRange, drilldown = null) => {
    filtersRef.current = nextFilters;
    customDateRangeRef.current = nextDateRange;
    setFilters(nextFilters);
    setCustomDateRange(nextDateRange);
    setChartDrilldown(drilldown);
    setProposalCurrentPage(1);
    setBreakdownApiPage(1);
    persistState({ filters: nextFilters, customDateRange: nextDateRange });
    loadAnalysis(nextFilters, nextDateRange);
    if (activeTab === 'annual-revenue-projection') {
      setArpFetchNonce((currentValue) => currentValue + 1);
    }
  }, [activeTab, loadAnalysis]);

  // Set department filter for view_department so payload and state stay in sync
  useEffect(() => {
    if (can('view_department', 'analysis') && user?.departmentId) {
      setFilters((prev) => {
        const currentDept = Array.isArray(prev.department) ? prev.department : [];
        const deptId = String(user.departmentId);
        if (currentDept.length !== 1 || String(currentDept[0]) !== deptId) {
          return { ...prev, department: [user.departmentId] };
        }
        return prev;
      });
    }
  }, [can, user?.departmentId]);

  useEffect(() => {
    loadAnalysis();
  }, []);

  useEffect(() => {
    if (activeTab !== 'annual-revenue-projection') return undefined;
    let cancelled = false;
    setAnnualProjectionLoading(true);
    setAnnualProjectionError(null);
    const viewParams = {};
    if (!can('view_global', 'analysis')) {
      if (can('view_department', 'analysis') && user?.departmentId) viewParams.viewDepartmentId = user.departmentId;
      else if (can('view_own', 'analysis') && user?.id) viewParams.viewOwnUserId = user.id;
    }
    // Map selected currency code to backend currencyId (if available)
    try {
      const selectedCode = (currency || '').toString().trim().toUpperCase();
      if (selectedCode) {
        const found = (currenciesList || []).find(c => {
          const code = (c.currencyCode || '').toString().trim().toUpperCase();
          return code === selectedCode;
        });
        if (found && found.id != null) {
          viewParams.currencyId = found.id;
        }
      }
    } catch (e) {
      // ignore mapping errors
    }
    const params = buildAnnualRevenueProjectionParams(
      filtersRef.current,
      customDateRangeRef.current,
      viewParams,
      { page: breakdownApiPage, size: breakdownApiPageSize }
    );
    getAnnualRevenueProjectionAnalysis(params)
      .then((data) => {
        if (!cancelled) setAnnualProjectionState(data);
      })
      .catch((err) => {
        if (cancelled) return;
        const backendMsg = err?.response?.data?.message;
        const axiosMsg = err?.message;
        const fallback = 'Failed to load annual revenue projection.';
        const emptyOrGeneric = !backendMsg || /no message available|message not found/i.test(String(backendMsg));
        setAnnualProjectionError(emptyOrGeneric ? (axiosMsg && !/network error/i.test(axiosMsg) ? axiosMsg : fallback) : backendMsg);
        setAnnualProjectionState(null);
      })
      .finally(() => {
        if (!cancelled) setAnnualProjectionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, breakdownApiPage, breakdownApiPageSize, arpFetchNonce, can, user]);

  const filterOptions = analysisData.filterOptions || {};
  const sourcesData = filterOptions.sources || [];
  const businessUnitsData = filterOptions.businessUnits || [];
  const sectorsData = filterOptions.sectors || [];
  const standardsData = filterOptions.standards || [];
  const salesUsersData = filterOptions.salesUsers || [];
  const proposalStagesData = filterOptions.proposalStages || [];

  // Build userId → { deptId, status } lookup from HRIS users for cascade + inactive badge
  const analysisUserDeptLookup = useMemo(() => {
    const map = new Map();
    allAnalysisUsers.forEach(u => {
      const deptId = u.departmentId || u.departementId;
      if (u.id != null) map.set(String(u.id), { deptId: deptId != null ? String(deptId) : null, status: u.status });
    });
    return map;
  }, [allAnalysisUsers]);

  // Cascade: filter salesCreator by selected department(s), mark inactive
  const cascadedSalesCreatorOptions = useMemo(() => {
    const selectedDepts = Array.isArray(filters.department) ? filters.department.filter(d => d !== 'all').map(String) : [];
    const base = salesUsersData.map(u => {
      const entry = analysisUserDeptLookup.get(String(u.id));
      return entry?.status === '0' ? { ...u, name: `${u.name} (Inactive)` } : u;
    });
    if (!selectedDepts.length) return base;
    return base.filter(u => {
      const entry = analysisUserDeptLookup.get(String(u.id));
      return entry?.deptId != null && selectedDepts.includes(entry.deptId);
    });
  }, [filters.department, salesUsersData, analysisUserDeptLookup]);
  const [branchesData, setBranchesData] = useState([]);
  const [taggingOptionsData, setTaggingOptionsData] = useState([]);

  // Competitor and lost reason options (fetched separately)
  const [competitorOptions, setCompetitorOptions] = useState([]);
  const [lostReasonOptions, setLostReasonOptions] = useState([]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [competitorRes, reasonRes, branchesRes, taggingRes] = await Promise.all([
          api.get('/crm/lead-competitors').catch(() => ({ data: [] })),
          api.get('/crm/reason-loss/all').catch(() => ({ data: [] })),
          api.get('/branches').catch(() => ({ data: [] })),
          api.get('/md/tagging', { params: { moduleKey: 'companies' } }).catch(() => ({ data: [] }))
        ]);
        setCompetitorOptions((competitorRes.data || []).map(c => ({ id: String(c.id ?? c.value ?? ''), name: c.name ?? c.label ?? '' })));
        setLostReasonOptions((reasonRes.data || []).map(r => ({ id: r.id, name: r.name })));
        const branchData = branchesRes.data?.data ?? branchesRes.data ?? [];
        setBranchesData(Array.isArray(branchData) ? branchData : []);
        const taggingData = taggingRes.data?.data ?? taggingRes.data ?? [];
        setTaggingOptionsData((Array.isArray(taggingData) ? taggingData : []).map((item) => ({
          id: String(item.id ?? item.value ?? ''),
          name: item.name ?? item.label ?? String(item.id ?? item.value ?? '')
        })));
      } catch (_) {}
    };
    fetchFilterOptions();
  }, []);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDepartmentMonthClick = React.useCallback((chartKey, chartTitle, series, months, config) => {
    const { seriesIndex, dataPointIndex } = config || {};
    if (seriesIndex == null || dataPointIndex == null || dataPointIndex < 0) {
      return;
    }

    const selectedSeries = series?.[seriesIndex];
    const monthLabel = months?.[dataPointIndex];
    const departmentOption = findOptionByLabel(businessUnitsData, selectedSeries?.name);
    if (!departmentOption) {
      return;
    }

    setChartSelections((prev) => ({
      ...prev,
      [chartKey]: { seriesIndex, dataPointIndex },
    }));

    const nextFilters = {
      ...filtersRef.current,
      department: [String(departmentOption.id)],
    };
    let nextDateRange = { ...customDateRangeRef.current };
    const appliedFilters = [`Department = ${departmentOption.name || selectedSeries?.name || 'Selected department'}`];

    const monthDrilldown = buildMonthlyDrilldownRange({
      period: filtersRef.current.period,
      customDateRange: customDateRangeRef.current,
      monthLabel,
      now: new Date(),
    });

    if (monthDrilldown.dateRange) {
      nextFilters.period = 'custom';
      nextDateRange = monthDrilldown.dateRange;
      appliedFilters.push(`Period = ${monthDrilldown.dateRange.startDate} to ${monthDrilldown.dateRange.endDate}`);
    }

    applyFiltersAndReload(nextFilters, nextDateRange, {
      source: chartTitle,
      selection: `${selectedSeries?.name || 'Department'} • ${monthLabel || 'Selected month'}`,
      appliedFilters,
      note: monthDrilldown.note,
    });
  }, [applyFiltersAndReload, businessUnitsData]);

  const handleOptionBarClick = React.useCallback((chartKey, chartTitle, labels, options, filterKey, config) => {
    const { dataPointIndex } = config || {};
    if (dataPointIndex == null || dataPointIndex < 0) {
      return;
    }

    const selectedLabel = labels?.[dataPointIndex];
    const option = findOptionByLabel(options, selectedLabel);
    if (!option?.id) {
      return;
    }

    setChartSelections((prev) => ({
      ...prev,
      [chartKey]: { dataPointIndex },
    }));

    const nextFilters = {
      ...filtersRef.current,
      [filterKey]: [String(option.id)],
    };

    applyFiltersAndReload(nextFilters, { ...customDateRangeRef.current }, {
      source: chartTitle,
      selection: String(selectedLabel || option.name || 'Selected value'),
      appliedFilters: [`${filterKey} = ${option.name || option.label || selectedLabel}`],
      note: null,
    });
  }, [applyFiltersAndReload]);

  const handleResetFilter = () => {
    const defaultDepartment =
      can('view_department', 'analysis') && user?.departmentId ? [user.departmentId] : [];
    const nextFilters = {
      period: 'this_year',
      salesCreator: [],
      department: defaultDepartment,
      sector: [],
      leadSource: [],
      standard: [],
      branchId: '',
      taggingIds: [],
      proposalStage: [],
      proposalApprovalStatus: '',
      competitor: [],
      lostReason: []
    };
    applyFiltersAndReload(nextFilters, { startDate: '', endDate: '' }, null);
    const el = document.getElementById('analysisFilterOffcanvas');
    if (el) window.bootstrap?.Offcanvas?.getInstance(el)?.hide();
  };

  const handleOpenFilter = () => {
    const el = document.getElementById('analysisFilterOffcanvas');
    if (!el || !window.bootstrap?.Offcanvas) return;
    window.bootstrap.Offcanvas.getOrCreateInstance(el, { backdrop: true, keyboard: true, scroll: false }).show();
  };

  const handleApplyFilter = () => {
    const el = document.getElementById('analysisFilterOffcanvas');
    if (el) window.bootstrap?.Offcanvas?.getInstance(el)?.hide();
    applyFiltersAndReload(filtersRef.current, customDateRangeRef.current, null);
  };

  const detailedLostFiltered = useMemo(() => {
    const list = analysisData.detailedLostProposals || [];
    const q = searchQueryLost.trim().toLowerCase();
    if (!q) return list;
    return list.filter((row) =>
      matchSearch(row, q, ['proposalNo', 'client', 'salesPerson', 'reason', 'competitor'])
    );
  }, [analysisData.detailedLostProposals, searchQueryLost]);

  // Add year breakdown columns to the Detailed Lost Proposals table (`year_<YEAR>` from API).
  const detailedLostDataWithYearTotals = useMemo(() => {
    const rows = detailedLostFiltered.map((row) => ({ ...row }));

    const totalAmount = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const totalByYear = {};
    projectionYears.forEach((y) => {
      const key = `year_${y}`;
      totalByYear[key] = rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    });

    return [
      ...rows,
      {
        isStaticRow: true,
        id: 'lost-total-row',
        proposalNo: 'Total',
        client: '',
        salesPerson: '',
        amount: totalAmount,
        reason: '',
        competitor: '',
        ...totalByYear
      }
    ];
  }, [detailedLostFiltered, projectionYears]);

  const detailedProposalFiltered = useMemo(() => {
    const list = analysisData.detailedProposals || [];
    const q = searchQueryProposal.trim().toLowerCase();
    if (!q) return list;
    return list.filter((row) =>
      matchSearch(row, q, ['proposalNo', 'client', 'projectNumber', 'salesPerson', 'product', 'stage', 'date'])
    );
  }, [analysisData.detailedProposals, searchQueryProposal]);

  useEffect(() => {
    setProposalCurrentPage(1);
  }, [searchQueryProposal]);

  // Detailed Proposal Data: current page slice + Sub Total (current page) + Total (all filtered)
  const detailedProposalDataWithTotal = useMemo(() => {
    const start = (proposalCurrentPage - 1) * proposalRowsPerPage;
    const getYearFromDate = (dateValue) => {
      const s = String(dateValue ?? '').trim();
      if (!s) return null;
      const m = s.match(/(\d{4})/);
      if (m && m[1]) {
        const y = Number(m[1]);
        return Number.isFinite(y) ? y : null;
      }
      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? null : d.getFullYear();
    };

    const enrichRowWithYear = (r) => {
      const out = { ...r };
      const yv = r.yearValues;
      if (yv && typeof yv === 'object' && !Array.isArray(yv) && Object.keys(yv).length > 0) {
        // Map yearValues { "2024": 5000, "2025": 3000 } → year_2024, year_2025 flat keys
        Object.entries(yv).forEach(([year, value]) => {
          out[`year_${year}`] = Number(value) || 0;
        });
        return out;
      }
      const dateYear = getYearFromDate(r.date);
      projectionYears.forEach((y) => {
        const key = `year_${y}`;
        if (out[key] == null) {
          out[key] = dateYear === y ? (r.amount || 0) : 0;
        }
      });
      return out;
    };

    const enrichedFiltered = detailedProposalFiltered.map(enrichRowWithYear);
    const pageRows = enrichedFiltered.slice(start, start + proposalRowsPerPage);
    const subAmount = pageRows.reduce((s, r) => s + (r.amount || 0), 0);
    const subExpense = pageRows.reduce((s, r) => s + (typeof r.totalExpense === 'number' ? r.totalExpense : 0), 0);
    const totalAmount = enrichedFiltered.reduce((s, r) => s + (r.amount || 0), 0);
    const totalExpense = enrichedFiltered.reduce((s, r) => s + (typeof r.totalExpense === 'number' ? r.totalExpense : 0), 0);

    const subByYear = {};
    projectionYears.forEach((y) => {
      const key = `year_${y}`;
      subByYear[key] = pageRows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    });
    const totalByYear = {};
    projectionYears.forEach((y) => {
      const key = `year_${y}`;
      totalByYear[key] = enrichedFiltered.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    });
    return [
      ...pageRows,
      {
        isStaticRow: true,
        id: 'subtotal-row',
        proposalNo: 'Sub Total',
        client: '',
        projectNumber: '',
        salesPerson: '',
        product: '',
        tags: [],
        amount: subAmount,
        totalExpense: subExpense,
        expenseRed: false,
        stage: '',
        date: '',
        ...subByYear
      },
      {
        isStaticRow: true,
        id: 'total-row',
        proposalNo: 'Total',
        client: '',
        projectNumber: '',
        salesPerson: '',
        product: '',
        tags: [],
        amount: totalAmount,
        totalExpense: totalExpense,
        expenseRed: false,
        stage: '',
        date: '',
        ...totalByYear
      }
    ];
  }, [detailedProposalFiltered, proposalCurrentPage, proposalRowsPerPage, projectionYears]);

  const columnsLost = useMemo(
    () => [
      {
        key: 'proposalNo',
        label: 'Proposal #',
        render: (value, row) =>
          row.isStaticRow ? (
            value
          ) : (
            <Link to={`/proposals/detail/${row.leadId}`} className="text-primary text-decoration-none">
              {value}
            </Link>
          )
      },
      {
        key: 'client',
        label: 'Client',
        render: (value) =>
          value ? (
            <div
              style={{
                maxWidth: '220px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={value}
            >
              {value}
            </div>
          ) : (
            '—'
          )
      },
      { key: 'salesPerson', label: 'Sales Person' },
      {
        key: 'amount',
        label: 'Amount',
        className: 'text-end',
        render: (value) => (value != null ? formatCurrencyVal(value) : '—')
      },
      { key: 'reason', label: 'Reason', render: (value, row) => (row.isStaticRow ? '' : <span className="text-danger">{value}</span>) },
      { key: 'competitor', label: 'Competitor(s)' },
      ...projectionYears.map((y, i) => ({
        key: `year_${y}`,
        label: String(y),
        className: `text-end ${i === 0 ? 'bg-blue-lt' : ''}`,
        render: (value) => (value != null && value !== '' ? formatCurrencyVal(value) : '—')
      }))
    ],
    [projectionYears]
  );

  const columnsProposal = useMemo(
    () => [
      {
        key: 'proposalNo',
        label: 'Proposal #',
        render: (value, row) =>
          row.isStaticRow ? (
            value
          ) : (
            <Link to={`/proposals/detail/${row.leadId ?? row.id}`} className="text-primary text-decoration-none">
              {value}
            </Link>
          )
      },
      {
        key: 'client',
        label: 'Client',
        render: (value) =>
          value ? (
            <div
              style={{
                maxWidth: '220px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={value}
            >
              {value}
            </div>
          ) : (
            '—'
          )
      },
      { key: 'projectNumber', label: 'Project Number' },
      { key: 'salesPerson', label: 'Sales Person' },
      { key: 'product', label: 'Product' },
      {
        key: 'tags',
        label: 'Tags',
        render: (value, row) => {
          if (row.isStaticRow) return null;
          if (!row.tags || !row.tags.length) return <span className="text-muted">—</span>;
          return (
            <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
              {row.tags.map((tag, i) => (
                <span
                  key={i}
                  className={`badge ${tag.variant === 'warning' ? 'bg-yellow-lt' : 'bg-blue-lt'} text-wrap`}
                  title={tag.label}
                  style={{ fontSize: '0.7rem' }}
                >
                  {tag.label}
                </span>
              ))}
            </span>
          );
        }
      },
      {
        key: 'amount',
        label: 'Amount',
        className: 'text-end',
        render: (value) => (value != null ? formatCurrencyVal(value) : '—')
      },
      {
        key: 'totalExpense',
        label: 'Total Expense',
        className: 'text-end',
        render: (value, row) => {
          if (row.isStaticRow) return <span className="text-danger">{value != null ? formatCurrencyVal(value) : '—'}</span>;
          const num = row.totalExpense != null && typeof row.totalExpense === 'number' ? row.totalExpense : null;
          const str = num != null ? formatCurrencyVal(num) : '—';
          return <span className="text-danger">{str}</span>;
        }
      },
      { key: 'stage', label: 'Stage' },
      { key: 'date', label: 'Date' },
      ...projectionYears.map((y, i) => ({
        key: `year_${y}`,
        label: String(y),
        className: `text-end ${i === 0 ? 'bg-blue-lt' : ''}`,
        render: (value) => (value != null && value !== '' ? formatCurrencyVal(value) : '—')
      }))
    ],
    [projectionYears]
  );

  const rawBreakdownList = annualProjectionState?.detailedAnnualBreakdownPaginated?.items ?? [];
  const detailedAnnualBreakdownList = useMemo(
    () =>
      rawBreakdownList.map((row) => {
        const out = { ...row };
        projectionYears.forEach((y) => {
          const key = `year_${y}`;
          if (out[key] == null && row[`year${y}`] != null) out[key] = row[`year${y}`];
        });
        return out;
      }),
    [rawBreakdownList, projectionYears]
  );
  const breakdownDataWithTotals = useMemo(() => {
    const pageRows = detailedAnnualBreakdownList;
    const paginated = annualProjectionState?.detailedAnnualBreakdownPaginated;
    const totalItems = paginated?.totalItems ?? 0;
    if (!pageRows.length && totalItems === 0) return [];

    const sum = (arr, key) => arr.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    const subTotalValue = sum(pageRows, 'totalValue');
    const subByYear = {};
    projectionYears.forEach((y) => {
      const key = `year_${y}`;
      subByYear[key] = sum(pageRows, key);
    });

    const rows = [
      ...pageRows,
      {
        isStaticRow: true,
        id: 'breakdown-subtotal',
        proposalNo: 'Sub Total (Page)',
        client: '',
        totalValue: subTotalValue,
        ...subByYear
      }
    ];

    const useServerGrand =
      totalItems > 0 && paginated?.grandTotalsByYear != null && typeof paginated.grandTotalsByYear === 'object';
    if (useServerGrand) {
      const grandByYear = {};
      projectionYears.forEach((y) => {
        const key = `year_${y}`;
        grandByYear[key] = Number(paginated.grandTotalsByYear[String(y)]) || 0;
      });
      rows.push({
        isStaticRow: true,
        id: 'breakdown-grandtotal',
        proposalNo: 'GRAND TOTAL',
        client: '',
        totalValue: Number(paginated.grandTotalValue) || 0,
        ...grandByYear
      });
    } else {
      const allRowsOnOnePage = totalItems > 0 && pageRows.length === totalItems;
      if (allRowsOnOnePage && pageRows.length > 0) {
        const grandTotalValue = sum(pageRows, 'totalValue');
        const grandByYear = {};
        projectionYears.forEach((y) => {
          const key = `year_${y}`;
          grandByYear[key] = sum(pageRows, key);
        });
        rows.push({
          isStaticRow: true,
          id: 'breakdown-grandtotal',
          proposalNo: 'GRAND TOTAL',
          client: '',
          totalValue: grandTotalValue,
          ...grandByYear
        });
      }
    }

    return rows;
  }, [detailedAnnualBreakdownList, projectionYears, annualProjectionState?.detailedAnnualBreakdownPaginated]);

  const columnsBreakdown = useMemo(
    () => [
      {
        key: 'proposalNo',
        label: 'Proposal #',
        render: (value, row) =>
          row.isStaticRow ? (
            value
          ) : (
            <Link to={`/proposals/detail/${row.leadId ?? row.id}`} className="text-primary text-decoration-none">
              {value}
            </Link>
          )
      },
      {
        key: 'client',
        label: 'Client',
        render: (value) =>
          value ? (
            <div
              style={{
                maxWidth: '220px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={value}
            >
              {value}
            </div>
          ) : (
            '—'
          )
      },
      {
        key: 'totalValue',
        label: 'Total Value',
        className: 'text-end',
        render: (value) => (value != null && value !== '' ? formatCurrencyVal(value) : '—')
      },
      ...projectionYears.map((y, i) => ({
        key: `year_${y}`,
        label: String(y),
        className: `text-end ${i === 0 ? 'bg-blue-lt' : ''}`,
        render: (value) => (value != null && value !== '' ? formatCurrencyVal(value) : '—')
      }))
    ],
    [projectionYears]
  );

  const wonValueSeries = useMemo(
    () => (analysisData.wonValueByDepartment?.series || []).map(({ name, data }) => ({ name, data })),
    [analysisData.wonValueByDepartment]
  );
  const lostRateSeries = useMemo(
    () => (analysisData.lostRateByDepartment?.series || []).map(({ name, data }) => ({ name, data })),
    [analysisData.lostRateByDepartment]
  );

  const wonValueOptions = useMemo(
    () => ({
      chart: {
        type: 'line',
        height: 320,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: true, easing: 'easeout', speed: 420 },
        events: {
          dataPointSelection: (_event, _chartContext, config) =>
            handleDepartmentMonthClick(
              'wonValueByDepartment',
              'Total Won Value by Department',
              wonValueSeries,
              analysisData.wonValueByDepartment?.months || MONTHS,
              config
            ),
        }
      },
      stroke: { curve: 'smooth', width: 4, lineCap: 'round' },
      colors: getDepartmentColors(analysisData.wonValueByDepartment?.series),
      dataLabels: { enabled: false },
      markers: { size: 5, strokeWidth: 0, hover: { sizeOffset: 4 } },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.12,
          opacityFrom: 0.12,
          opacityTo: 0.01,
          stops: [0, 90, 100]
        }
      },
      xaxis: {
        categories: analysisData.wonValueByDepartment?.months || MONTHS,
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
    yaxis: {
      labels: {
        formatter: (val) => formatCurrencyShort(val)
      }
    },
    grid: {
      borderColor: '#d7e1ec',
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } }
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      fontWeight: 500,
      markers: { width: 10, height: 10, radius: 12 }
    },
    states: {
      hover: { filter: { type: 'lighten', value: 0.08 } },
      active: { filter: { type: 'darken', value: 0.12 } }
    },
    tooltip: {
      theme: 'light',
      shared: true,
      y: {
        formatter: (val) => formatCurrencyShort(val)
      }
    }
    }),
    [analysisData.wonValueByDepartment, handleDepartmentMonthClick, wonValueSeries]
  );

  const lostRateOptions = useMemo(() => ({
    chart: {
      type: 'line',
      height: 320,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true, easing: 'easeout', speed: 420 },
      events: {
        dataPointSelection: (_event, _chartContext, config) =>
          handleDepartmentMonthClick(
            'lostRateByDepartment',
            'Proposal Lost Rate by Department',
            lostRateSeries,
            analysisData.lostRateByDepartment?.months || MONTHS,
            config
          ),
      }
    },
    stroke: { curve: 'smooth', width: 4, lineCap: 'round' },
    colors: getDepartmentColors(analysisData.lostRateByDepartment?.series),
    dataLabels: { enabled: false },
    markers: { size: 5, strokeWidth: 0, hover: { sizeOffset: 4 } },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.1,
        opacityFrom: 0.1,
        opacityTo: 0.01,
        stops: [0, 90, 100]
      }
    },
    xaxis: {
      categories: analysisData.lostRateByDepartment?.months || MONTHS,
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: (val) => `${formatPercent2(val)}%`
      }
    },
    grid: {
      borderColor: '#d7e1ec',
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } }
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      fontWeight: 500,
      markers: { width: 10, height: 10, radius: 12 }
    },
    states: {
      hover: { filter: { type: 'lighten', value: 0.08 } },
      active: { filter: { type: 'darken', value: 0.12 } }
    },
    tooltip: {
      theme: 'light',
      shared: true,
      y: { formatter: (val) => `${formatPercent2(val)}%` }
    }
  }), [analysisData.lostRateByDepartment, handleDepartmentMonthClick, lostRateSeries]);

  const wonValueInsight = useMemo(
    () => buildLineChartInsight({
      months: analysisData.wonValueByDepartment?.months || MONTHS,
      series: wonValueSeries,
      selectedSeriesIndex: null,
      selectedPointIndex: null,
    }),
    [analysisData.wonValueByDepartment, wonValueSeries]
  );
  const lostRateInsight = useMemo(
    () => buildLineChartInsight({
      months: analysisData.lostRateByDepartment?.months || MONTHS,
      series: lostRateSeries,
      selectedSeriesIndex: null,
      selectedPointIndex: null,
    }),
    [analysisData.lostRateByDepartment, lostRateSeries]
  );
  const proposalFunnelInsight = useMemo(
    () => buildBarChartInsight({
      labels: analysisData.proposalFunnel?.labels || [],
      data: analysisData.proposalFunnel?.data || [],
      selectedIndex: null,
    }),
    [analysisData.proposalFunnel]
  );
  const leadSourceInsight = useMemo(
    () => buildBarChartInsight({
      labels: analysisData.prospectsByLeadSource?.labels || [],
      data: analysisData.prospectsByLeadSource?.data || [],
      selectedIndex: null,
    }),
    [analysisData.prospectsByLeadSource]
  );
  const productWinRateInsight = useMemo(
    () => buildBarChartInsight({
      labels: analysisData.productWinRate?.labels || [],
      data: analysisData.productWinRate?.data || [],
      selectedIndex: null,
    }),
    [analysisData.productWinRate]
  );
  const lostValueInsight = useMemo(
    () => buildBarChartInsight({
      labels: analysisData.lostValueByReason?.labels || [],
      data: analysisData.lostValueByReason?.data || [],
      selectedIndex: null,
    }),
    [analysisData.lostValueByReason]
  );
  const lostCountInsight = useMemo(
    () => buildBarChartInsight({
      labels: analysisData.lostCountByReason?.labels || [],
      data: analysisData.lostCountByReason?.data || [],
      selectedIndex: null,
    }),
    [analysisData.lostCountByReason]
  );
  const sectorChartData = useMemo(() => {
    const rows = Array.isArray(analysisData.topSectors) ? analysisData.topSectors : [];
    return {
      labels: rows.map((row) => row?.sector || 'Unknown Sector'),
      data: rows.map((row) => Number(row?.proposals) || 0),
    };
  }, [analysisData.topSectors]);
  const sectorInsight = useMemo(
    () => buildBarChartInsight({
      labels: sectorChartData.labels,
      data: sectorChartData.data,
      selectedIndex: null,
    }),
    [sectorChartData]
  );
  const handleSectorListClick = React.useCallback((index) => {
    handleOptionBarClick(
      'sectorsByProposalCount',
      'Sectors by Proposal Count',
      sectorChartData.labels,
      sectorsData,
      'sector',
      { dataPointIndex: index }
    );
  }, [handleOptionBarClick, sectorChartData.labels, sectorsData]);

  const proposalFunnelOptions = useMemo(() => ({
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: { enabled: true, easing: 'easeout', speed: 420 },
      events: {
        dataPointSelection: (_event, _chartContext, config) =>
          handleOptionBarClick(
            'proposalFunnel',
            'Proposal Funnel',
            analysisData.proposalFunnel?.labels || [],
            proposalStagesData,
            'proposalStage',
            config
          ),
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '66%',
        borderRadius: 8,
        distributed: true,
        dataLabels: { position: 'bottom' }
      }
    },
    colors: getInteractiveBarColors((analysisData.proposalFunnel?.data || []).length),
    dataLabels: { enabled: true, offsetX: 10, style: { fontSize: '12px' } },
    xaxis: { categories: analysisData.proposalFunnel?.labels || [] },
    grid: { xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    legend: { show: false },
    states: {
      hover: { filter: { type: 'lighten', value: 0.08 } },
      active: { filter: { type: 'darken', value: 0.1 } },
    },
    tooltip: { theme: 'light' }
  }), [analysisData.proposalFunnel, handleOptionBarClick, proposalStagesData]);

  const leadSourceOptions = useMemo(() => ({
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: { enabled: true, easing: 'easeout', speed: 420 },
      events: {
        dataPointSelection: (_event, _chartContext, config) =>
          handleOptionBarClick(
            'prospectsByLeadSource',
            'Prospects by Lead Source',
            analysisData.prospectsByLeadSource?.labels || [],
            sourcesData,
            'leadSource',
            config
          ),
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '66%',
        borderRadius: 8,
        distributed: true,
        dataLabels: { position: 'bottom' }
      }
    },
    colors: getInteractiveBarColors((analysisData.prospectsByLeadSource?.data || []).length),
    dataLabels: { enabled: true, offsetX: 10, style: { fontSize: '12px' } },
    xaxis: { categories: analysisData.prospectsByLeadSource?.labels || [] },
    grid: { xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    legend: { show: false },
    states: {
      hover: { filter: { type: 'lighten', value: 0.08 } },
      active: { filter: { type: 'darken', value: 0.1 } },
    },
    tooltip: { theme: 'light' }
  }), [analysisData.prospectsByLeadSource, handleOptionBarClick, sourcesData]);

  const productWinRateOptions = useMemo(() => ({
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: { enabled: true, easing: 'easeout', speed: 420 },
      events: {
        dataPointSelection: (_event, _chartContext, config) =>
          handleOptionBarClick(
            'productWinRate',
            'Top 5 Product Win Rate',
            analysisData.productWinRate?.labels || [],
            standardsData,
            'standard',
            config
          ),
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '66%',
        borderRadius: 8,
        distributed: true,
        dataLabels: { position: 'bottom' }
      }
    },
    colors: getInteractiveBarColors((analysisData.productWinRate?.data || []).length),
    dataLabels: {
      enabled: true,
      offsetX: 10,
      formatter: (val) => `${formatPercent2(val)}%`,
      style: { fontSize: '12px' }
    },
    xaxis: { categories: analysisData.productWinRate?.labels || [], max: 100 },
    grid: { xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    legend: { show: false },
    states: {
      hover: { filter: { type: 'lighten', value: 0.08 } },
      active: { filter: { type: 'darken', value: 0.1 } },
    },
    tooltip: {
      theme: 'light',
      y: { formatter: (val) => `${formatPercent2(val)}%` }
    }
  }), [analysisData.productWinRate, handleOptionBarClick, standardsData]);

  const lostValueByReasonOptions = useMemo(() => ({
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: { enabled: true, easing: 'easeout', speed: 420 },
      events: {
        dataPointSelection: (_event, _chartContext, config) =>
          handleOptionBarClick(
            'lostValueByReason',
            'Lost Value by Reason',
            analysisData.lostValueByReason?.labels || [],
            lostReasonOptions,
            'lostReason',
            config
          ),
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '66%',
        borderRadius: 8,
        distributed: true,
        dataLabels: { position: 'bottom' }
      }
    },
    colors: getInteractiveBarColors((analysisData.lostValueByReason?.data || []).length),
    dataLabels: {
      enabled: true,
      offsetX: 10,
      style: { fontSize: '12px' },
      formatter: (val) => formatCurrencyShort(val)
    },
    xaxis: {
      categories: analysisData.lostValueByReason?.labels || [],
      labels: { formatter: (val) => formatCurrencyShort(val) }
    },
    grid: { xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    legend: { show: false },
    states: {
      hover: { filter: { type: 'lighten', value: 0.08 } },
      active: { filter: { type: 'darken', value: 0.1 } },
    },
    tooltip: {
      theme: 'light',
      y: { formatter: (val) => formatCurrencyShort(val) }
    }
  }), [analysisData.lostValueByReason, handleOptionBarClick, lostReasonOptions]);

  const lostCountByReasonOptions = useMemo(() => ({
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: { enabled: true, easing: 'easeout', speed: 420 },
      events: {
        dataPointSelection: (_event, _chartContext, config) =>
          handleOptionBarClick(
            'lostCountByReason',
            'Lost Count by Reason',
            analysisData.lostCountByReason?.labels || [],
            lostReasonOptions,
            'lostReason',
            config
          ),
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '66%',
        borderRadius: 8,
        distributed: true,
        dataLabels: { position: 'bottom' }
      }
    },
    colors: getInteractiveBarColors((analysisData.lostCountByReason?.data || []).length),
    dataLabels: { enabled: true, offsetX: 10, style: { fontSize: '12px' } },
    xaxis: { categories: analysisData.lostCountByReason?.labels || [] },
    grid: { xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    legend: { show: false },
    states: {
      hover: { filter: { type: 'lighten', value: 0.08 } },
      active: { filter: { type: 'darken', value: 0.1 } },
    },
    tooltip: { theme: 'light' }
  }), [analysisData.lostCountByReason, handleOptionBarClick, lostReasonOptions]);

  const annualRevenueProjectionSeries = useMemo(() => {
    if (annualProjectionError) return [];
    const fromApi = annualProjectionState?.annualRevenueProjection?.series;
    const len = projectionYears.length;
    if (
      Array.isArray(fromApi) &&
      fromApi.length > 0 &&
      fromApi.every((s) => Array.isArray(s.data) && s.data.length === len)
    ) {
      return fromApi.map(({ name, data }) => ({ name, data }));
    }
    if (annualProjectionState?.annualRevenueProjection && Array.isArray(fromApi) && fromApi.length === 0) {
      return [];
    }
    return [];
  }, [
    annualProjectionError,
    annualProjectionState?.annualRevenueProjection,
    projectionYears
  ]);

  const handleAnnualProjectionClick = React.useCallback((config) => {
    const { seriesIndex, dataPointIndex } = config || {};
    if (seriesIndex == null || dataPointIndex == null || dataPointIndex < 0) {
      return;
    }

    const selectedSeries = annualRevenueProjectionSeries?.[seriesIndex];
    const departmentOption = findOptionByLabel(businessUnitsData, selectedSeries?.name);
    if (!departmentOption?.id) {
      return;
    }

    setChartSelections((prev) => ({
      ...prev,
      annualRevenueProjection: { seriesIndex, dataPointIndex },
    }));

    const yearLabel = projectionYears?.[dataPointIndex];
    const nextFilters = {
      ...filtersRef.current,
      department: [String(departmentOption.id)],
    };

    applyFiltersAndReload(nextFilters, { ...customDateRangeRef.current }, {
      source: 'Annual Revenue Projection by Department',
      selection: `${selectedSeries?.name || 'Department'} • ${yearLabel || 'Selected year'}`,
      appliedFilters: [`Department = ${departmentOption.name || selectedSeries?.name || 'Selected department'}`],
      note: 'Projection click applies department drilldown only so future-year values stay traceable to the same backend model.',
    });
  }, [annualRevenueProjectionSeries, applyFiltersAndReload, businessUnitsData, projectionYears]);

  const annualRevenueProjectionOptions = useMemo(() => ({
    chart: {
      type: 'line',
      height: 320,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true, easing: 'easeout', speed: 420 },
      events: {
        dataPointSelection: (_event, _chartContext, config) => handleAnnualProjectionClick(config),
      }
    },
    stroke: { curve: 'smooth', width: 3 },
    colors: annualRevenueProjectionSeries.map(
      (_, i) =>
        annualProjectionState?.annualRevenueProjection?.series?.[i]?.color ??
        DEPARTMENT_COLORS[i % DEPARTMENT_COLORS.length]
    ),
    dataLabels: { enabled: false },
    markers: { size: 4, hover: { sizeOffset: 3 } },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.22,
        opacityFrom: 0.24,
        opacityTo: 0.03,
        stops: [0, 90, 100]
      }
    },
    xaxis: {
      categories: projectionYears,
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
      yaxis: {
        labels: {
          formatter: (val) => {
            // Use short currency formatter
            return formatCurrencyShort(val);
          }
        }
      },
    grid: {
      borderColor: '#e9ecef',
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } }
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center'
    },
      states: {
        hover: { filter: { type: 'lighten', value: 0.08 } },
        active: { filter: { type: 'darken', value: 0.12 } }
      },
      tooltip: {
        theme: 'light',
        shared: true,
        y: {
          formatter: (val) => formatCurrencyShort(val)
        }
      }
  }), [projectionYears, annualRevenueProjectionSeries, annualProjectionState?.annualRevenueProjection?.series, handleAnnualProjectionClick]);

  const annualTotalProposalWon = useMemo(
    () => (annualProjectionState?.topPerformers || []).reduce((s, r) => s + (r.won || 0), 0),
    [annualProjectionState?.topPerformers]
  );
  const annualRevenueInsight = useMemo(
    () => buildLineChartInsight({
      months: projectionYears.map(String),
      series: annualRevenueProjectionSeries,
      selectedSeriesIndex: null,
      selectedPointIndex: null,
    }),
    [projectionYears, annualRevenueProjectionSeries]
  );

  const canViewAnalysis =
    can('view_global', 'analysis') ||
    can('view_department', 'analysis') ||
    can('view_own', 'analysis');
  const canViewGlobalAnalysis = can('view_global', 'analysis');
  const showDepartmentFilter = canViewGlobalAnalysis;
  const showSalesCreatorFilter = canViewGlobalAnalysis || can('view_department', 'analysis');
  const activeFilterTags = useMemo(() => buildAnalysisFilterTags({
    filters,
    customDateRange,
    currency,
    salesUsersData,
    businessUnitsData,
    branchesData,
    sectorsData,
    sourcesData,
    standardsData,
    proposalStagesData,
    taggingOptionsData,
    competitorOptions,
    lostReasonOptions,
    showDepartmentFilter,
    showSalesCreatorFilter,
  }), [
    filters,
    customDateRange,
    currency,
    salesUsersData,
    businessUnitsData,
    branchesData,
    sectorsData,
    sourcesData,
    standardsData,
    proposalStagesData,
    taggingOptionsData,
    competitorOptions,
    lostReasonOptions,
    showDepartmentFilter,
    showSalesCreatorFilter,
  ]);
  const hasActiveFilters = activeFilterTags.length > 0;
  const periodLabelMap = useMemo(
    () => new Map(PERIOD_OPTIONS.map((option) => [option.id, option.name])),
    []
  );
  const accessScopeLabel = canViewGlobalAnalysis
    ? 'Access scope: global analysis data'
    : can('view_department', 'analysis')
      ? 'Access scope: your department data'
      : 'Access scope: your assigned analysis data';
  const awarenessTitle = chartDrilldown
    ? 'Chart-driven cross-filter is active'
    : hasActiveFilters
      ? 'Analysis is showing a filtered view'
      : filters.period === 'all'
        ? 'Analysis is showing all available time'
        : 'Analysis is showing the default view';
  const awarenessSummaryParts = useMemo(() => {
    const parts = [];
    const periodLabel = periodLabelMap.get(filters.period) || 'This Year';
    if (filters.period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
      parts.push(`Period: ${periodLabel} (${customDateRange.startDate} to ${customDateRange.endDate})`);
    } else {
      parts.push(`Period: ${periodLabel}`);
    }

    if (currency) {
      parts.push(`Currency view: ${String(currency).toUpperCase()}`);
    } else {
      parts.push('Currency view: IDR');
    }

    if (!hasActiveFilters && filters.period !== 'all') {
      parts.push('No additional filters beyond the default period are active');
    }

    if (!hasActiveFilters && filters.period === 'all') {
      parts.push('No additional filters are active');
    }

    parts.push(accessScopeLabel);

    return parts;
  }, [accessScopeLabel, currency, customDateRange.endDate, customDateRange.startDate, filters.period, hasActiveFilters, periodLabelMap]);
  const awarenessDetail = chartDrilldown
    ? `${chartDrilldown.source}: ${chartDrilldown.selection}`
    : hasActiveFilters
      ? `${activeFilterTags.length} active filter ${activeFilterTags.length === 1 ? 'rule' : 'rules'} are affecting every chart, KPI, and detailed list below.`
      : 'Every visible KPI, chart, and table below follows the same current analysis context.';

  const handleRemoveFilter = (filterKey) => {
    const baseFilters = { ...filtersRef.current };
    let nextFilters = baseFilters;
    let nextDateRange = { ...customDateRangeRef.current };
    switch (filterKey) {
      case 'period':
        nextFilters = { ...baseFilters, period: 'this_year' };
        nextDateRange = { startDate: '', endDate: '' };
        break;
      case 'currency':
        setCurrency('');
        return;
      case 'proposalApprovalStatus':
        nextFilters = { ...baseFilters, proposalApprovalStatus: '' };
        break;
      case 'salesCreator':
      case 'department':
      case 'sector':
      case 'leadSource':
      case 'standard':
      case 'taggingIds':
      case 'proposalStage':
      case 'competitor':
      case 'lostReason':
        nextFilters = { ...baseFilters, [filterKey]: [] };
        break;
      case 'branchId':
        nextFilters = { ...baseFilters, branchId: '' };
        break;
      default:
        return;
    }

    applyFiltersAndReload(nextFilters, nextDateRange, null);
  };
  if (capabilitiesLoading) {
    return <Loading />;
  }
  if (!canViewAnalysis) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="page-body" style={{ overflowX: 'hidden' }}>
      <div className="container-xl" style={{ overflowX: 'hidden' }}>
        <style>{`
          .dashboard-info-tooltip-wrapper {
            position: relative;
            display: inline-flex;
            align-items: center;
          }
          .dashboard-info-tooltip-wrapper::after {
            content: attr(data-tooltip);
            position: absolute;
            bottom: calc(100% + 8px);
            left: 50%;
            transform: translateX(-50%);
            background: #1e293b;
            color: #f8fafc;
            font-size: 12px;
            font-weight: 400;
            line-height: 1.5;
            padding: 7px 12px;
            border-radius: 6px;
            max-width: 280px;
            min-width: 180px;
            white-space: normal;
            text-align: left;
            z-index: 9999;
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.18s ease;
            box-shadow: 0 4px 16px rgba(0,0,0,0.18);
          }
          .dashboard-info-tooltip-wrapper::before {
            content: '';
            position: absolute;
            bottom: calc(100% + 2px);
            left: 50%;
            transform: translateX(-50%);
            border: 6px solid transparent;
            border-top-color: #1e293b;
            z-index: 9999;
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.18s ease;
          }
          .dashboard-info-tooltip-wrapper:hover::after,
          .dashboard-info-tooltip-wrapper:hover::before {
            opacity: 1;
          }
          .dashboard-info-icon:hover {
            color: #667eea !important;
          }
          .analysis-filter-summary {
            position: sticky;
            top: 0;
            z-index: 1025;
            margin-top: -4px;
            margin-bottom: 16px;
            padding: 10px 0 8px;
            background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 75%, rgba(255,255,255,0) 100%);
            backdrop-filter: blur(10px);
          }
          .analysis-sticky-stack {
            position: sticky;
            top: 0;
            z-index: 1025;
            margin-bottom: 18px;
            padding-top: 6px;
            background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 82%, rgba(255,255,255,0) 100%);
            backdrop-filter: blur(10px);
          }
          .analysis-awareness-bar {
            display: flex;
            gap: 16px;
            margin-bottom: 12px;
            padding: 14px 18px;
            border-radius: 20px;
            border: 1px solid rgba(148, 163, 184, 0.2);
            background: linear-gradient(135deg, rgba(248,250,252,0.98), rgba(239,246,255,0.96));
            box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
          }
          .analysis-awareness-copy {
            min-width: 0;
          }
          .analysis-awareness-kicker {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 11px;
            font-weight: 800;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #0369a1;
            margin-bottom: 4px;
          }
          .analysis-awareness-dot {
            width: 8px;
            height: 8px;
            border-radius: 999px;
            background: #14b8a6;
            box-shadow: 0 0 0 6px rgba(20,184,166,0.12);
          }
          .analysis-awareness-dot.is-loading {
            background: #f97316;
            box-shadow: 0 0 0 6px rgba(249,115,22,0.14);
            animation: analysisPulse 1.2s ease-in-out infinite;
          }
          .analysis-awareness-title {
            font-size: 1.02rem;
            font-weight: 700;
            color: #0f172a;
            margin-bottom: 4px;
          }
          .analysis-awareness-summary {
            font-size: 13px;
            color: #0f172a;
            line-height: 1.6;
          }
          .analysis-awareness-detail {
            margin-top: 6px;
            font-size: 12px;
            color: #475569;
            line-height: 1.5;
          }
          .analysis-chart-hint {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 12px;
            padding: 6px 12px;
            border-radius: 999px;
            background: linear-gradient(90deg, #eff6ff 0%, #ecfeff 100%);
            color: #0f172a;
            font-size: 12px;
            font-weight: 500;
            border: 1px solid #bfdbfe;
          }
          .analysis-chart-drilldown-banner {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 16px;
            margin-bottom: 18px;
            padding: 16px 18px;
            border-radius: 20px;
            background: linear-gradient(135deg, rgba(14, 165, 233, 0.12), rgba(20, 184, 166, 0.16));
            border: 1px solid rgba(14, 165, 233, 0.18);
            box-shadow: 0 12px 30px rgba(15, 23, 42, 0.07);
          }
          .analysis-chart-drilldown-kicker {
            font-size: 11px;
            font-weight: 800;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #0f766e;
            margin-bottom: 4px;
          }
          .analysis-chart-drilldown-title {
            font-size: 1rem;
            font-weight: 700;
            color: #0f172a;
            margin-bottom: 4px;
          }
          .analysis-chart-drilldown-summary {
            color: #0f172a;
            font-size: 13px;
            line-height: 1.6;
          }
          .analysis-chart-drilldown-note {
            margin-top: 6px;
            color: #475569;
            font-size: 12px;
            line-height: 1.5;
          }
          .analysis-chart-dismiss {
            border: none;
            background: rgba(255,255,255,0.8);
            color: #0f172a;
            border-radius: 999px;
            padding: 8px 12px;
            font-size: 12px;
            font-weight: 700;
            white-space: nowrap;
          }
          .analysis-chart-dismiss:hover {
            background: rgba(255,255,255,0.96);
          }
          .analysis-chart-insight {
            margin-top: 14px;
            padding: 14px;
            border-radius: 18px;
            border: 1px solid rgba(148, 163, 184, 0.22);
            background: linear-gradient(135deg, rgba(255,255,255,0.96), rgba(248,250,252,0.9));
            box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
          }
          .analysis-chart-insight-blue { background: linear-gradient(135deg, rgba(219, 234, 254, 0.95), rgba(239, 246, 255, 0.94)); }
          .analysis-chart-insight-cyan { background: linear-gradient(135deg, rgba(207, 250, 254, 0.95), rgba(240, 249, 255, 0.94)); }
          .analysis-chart-insight-teal { background: linear-gradient(135deg, rgba(204, 251, 241, 0.95), rgba(240, 253, 250, 0.94)); }
          .analysis-chart-insight-violet { background: linear-gradient(135deg, rgba(237, 233, 254, 0.95), rgba(245, 243, 255, 0.94)); }
          .analysis-chart-insight-orange { background: linear-gradient(135deg, rgba(255, 237, 213, 0.96), rgba(255, 247, 237, 0.94)); }
          .analysis-chart-insight-amber { background: linear-gradient(135deg, rgba(254, 243, 199, 0.96), rgba(255, 251, 235, 0.94)); }
          .analysis-chart-insight-rose { background: linear-gradient(135deg, rgba(255, 228, 230, 0.95), rgba(255, 241, 242, 0.94)); }
          .analysis-chart-insight-sky { background: linear-gradient(135deg, rgba(224, 242, 254, 0.96), rgba(240, 249, 255, 0.94)); }
          .analysis-chart-insight-header {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 12px;
          }
          .analysis-chart-insight-kicker {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 6px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #475569;
          }
          .analysis-chart-insight-title {
            font-size: 1rem;
            font-weight: 700;
            color: #0f172a;
          }
          .analysis-chart-insight-summary {
            margin-top: 8px;
            color: #334155;
            font-size: 13px;
            line-height: 1.6;
          }
          .analysis-chart-stat-grid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 10px;
            margin-top: 12px;
          }
          .analysis-chart-stat {
            padding: 10px 12px;
            border-radius: 14px;
            background: rgba(255,255,255,0.78);
            border: 1px solid rgba(255,255,255,0.65);
          }
          .analysis-chart-stat-label {
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: #64748b;
            margin-bottom: 4px;
          }
          .analysis-chart-stat-value {
            font-size: 14px;
            font-weight: 700;
            color: #0f172a;
          }
          .analysis-chart-clear {
            border: none;
            border-radius: 999px;
            background: rgba(15, 23, 42, 0.08);
            color: #0f172a;
            padding: 6px 10px;
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
          }
          .analysis-chart-clear:hover {
            background: rgba(15, 23, 42, 0.14);
          }
          .analysis-card-shell {
            position: relative;
            transition: opacity 0.2s ease, transform 0.2s ease;
          }
          .analysis-card-shell.is-loading {
            opacity: 0.68;
          }
          .analysis-loading-overlay {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 16px;
            border-radius: 18px;
            background: rgba(255,255,255,0.46);
            backdrop-filter: blur(2px);
            z-index: 4;
            pointer-events: none;
          }
          .analysis-loading-pill {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: 10px 14px;
            border-radius: 999px;
            background: rgba(15,23,42,0.84);
            color: #f8fafc;
            font-size: 12px;
            font-weight: 600;
            box-shadow: 0 12px 24px rgba(15, 23, 42, 0.18);
          }
          .analysis-table-caption {
            margin-bottom: 10px;
            font-size: 12px;
            color: #64748b;
          }
          .analysis-sector-list {
            max-height: 300px;
            overflow-y: auto;
            border-radius: 18px;
            border: 1px solid rgba(226, 232, 240, 0.95);
            background: linear-gradient(180deg, rgba(248,250,252,0.9), rgba(255,255,255,0.95));
          }
          .analysis-sector-row {
            cursor: pointer;
            transition: background-color 0.18s ease, transform 0.18s ease;
          }
          .analysis-sector-row:hover {
            background: rgba(14, 165, 233, 0.08);
          }
          .analysis-sector-row:active {
            transform: scale(0.997);
          }
          .analysis-sector-name {
            display: inline-block;
            max-width: 700px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            vertical-align: bottom;
          }
          .analysis-sector-pill {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 12px;
            padding: 8px 12px;
            border-radius: 999px;
            background: linear-gradient(90deg, rgba(236,253,245,0.95), rgba(240,249,255,0.95));
            border: 1px solid rgba(45, 212, 191, 0.2);
            color: #0f172a;
            font-size: 12px;
            font-weight: 600;
          }
          @keyframes analysisPulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.12); opacity: 0.7; }
          }
          @media (max-width: 768px) {
            .analysis-filter-summary,
            .analysis-sticky-stack {
              top: 0;
            }
            .analysis-chart-drilldown-banner {
              flex-direction: column;
            }
            .analysis-chart-stat-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>

        <div className="row g-2 align-items-center mb-4">
          <div className="col-md-3">
            <h1 className="page-title m-0">Analysis</h1>
          </div>
        </div>

        {analysisError && (
          <div className="alert alert-danger mb-4" role="alert">
            {analysisError}
          </div>
        )}

        <div className="d-flex align-items-center justify-content-between mb-3">
          <nav className="nav nav-segmented nav-2" role="tablist" aria-label="Analysis tabs">
            <button
              type="button"
              className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
              role="tab"
              aria-selected={activeTab === 'overview'}
              aria-current={activeTab === 'overview' ? 'page' : undefined}
              tabIndex={activeTab === 'overview' ? 0 : -1}
            >
              <LayoutDashboard className="icon nav-link-icon icon-2" size={24} strokeWidth={2} />
              Overview
            </button>
            <button
              type="button"
              className={`nav-link ${activeTab === 'annual-revenue-projection' ? 'active' : ''}`}
              onClick={() => setActiveTab('annual-revenue-projection')}
              role="tab"
              aria-selected={activeTab === 'annual-revenue-projection'}
              aria-current={activeTab === 'annual-revenue-projection' ? 'page' : undefined}
              tabIndex={activeTab === 'annual-revenue-projection' ? 0 : -1}
            >
              <TrendingUp className="icon nav-link-icon icon-2" size={24} strokeWidth={2} />
              Annual Revenue Projection
            </button>
          </nav>

          <button type="button" className="btn" onClick={handleOpenFilter}>
            <Funnel className="icon icon-tabler text-black" />
            Filter
          </button>
        </div>

        <div className="analysis-sticky-stack">
          <FilterAwarenessBar
            title={awarenessTitle}
            summary={awarenessSummaryParts.join(' • ')}
            detail={awarenessDetail}
            loading={isLoadingAnalysis}
          />

          {hasActiveFilters && (
            <div className="analysis-filter-summary">
              <FilterChips
                activeFilterTags={activeFilterTags}
                onRemoveFilter={handleRemoveFilter}
                onResetFilters={handleResetFilter}
              />
            </div>
          )}

          <ChartDrilldownBanner drilldown={chartDrilldown} onDismiss={dismissChartDrilldown} />
        </div>

        {activeTab === 'overview' && (
        <>
        {/* KPI cards — 5 columns on xl+, 2 on sm, 1 on xs */}
        <div className="row row-deck row-cards mb-4">
          <div className="col-12 col-sm-6 col-xl">
            <TotalProposalSummaryCard
              summary={analysisData.totalProposalSummary}
              loading={isLoadingAnalysis}
              infoTitle="Number of proposals in the selected period, grouped by stage."
            />
          </div>
          <div className="col-12 col-sm-6 col-xl">
            <KPICard
              title="Total Prospects"
              value={isLoadingAnalysis ? '—' : analysisData.kpi.totalProspects}
              description="Total prospects in period"
            />
          </div>
          <div className="col-12 col-sm-6 col-xl">
            <KPICard
              title="Proposal Win Rate"
              value={isLoadingAnalysis ? '—' : `${formatPercent2(analysisData.kpi.proposalWinRate)}%`}
              description="Percentage of proposals won"
            />
          </div>
          <div className="col-12 col-sm-6 col-xl">
            <KPICard
              title="Total Won Value"
              value={isLoadingAnalysis ? '—' : formatCurrencyVal(analysisData.kpi.totalWonValue)}
              description="Sum of all won proposals"
            />
          </div>
          <div className="col-12 col-sm-6 col-xl">
            <KPICard
              title="Total Expenses"
              value={isLoadingAnalysis ? '—' : formatCurrencyVal(analysisData.kpi.totalExpenses)}
              description="Sum of all recorded expenses"
            />
          </div>
        </div>

        {/* Charts */}
        <div className="row row-deck row-cards">
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body position-relative">
                <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="Total won value by department per month" /></div>
                <h3 className="card-title mb-3">Total Won Value by Department</h3>
                <ChartInteractionHint />
                <div className={`analysis-card-shell ${isLoadingAnalysis ? 'is-loading' : ''}`}>
                  <ReactApexChart
                    options={wonValueOptions}
                    series={wonValueSeries}
                    type="line"
                    height={320}
                  />
                  <LoadingOverlay show={isLoadingAnalysis} label="Refreshing won-value trends..." />
                </div>
                <ChartInsightPanel
                  accentClass="analysis-chart-insight-blue"
                  kicker={wonValueInsight?.isPinned ? 'Pinned insight' : 'Peak insight'}
                  title={wonValueInsight ? `${wonValueInsight.seriesName} in ${wonValueInsight.month}` : ''}
                  summary={wonValueInsight ? `${formatCurrencyVal(wonValueInsight.value)} contributed ${formatPercent2(wonValueInsight.shareAtPoint)}% of the selected month total.` : ''}
                  stats={wonValueInsight ? [
                    { label: 'Selected Value', value: formatCurrencyVal(wonValueInsight.value) },
                    { label: 'Month Total', value: formatCurrencyVal(wonValueInsight.totalAtPoint) },
                    { label: 'Series Peak', value: `${formatCurrencyVal(wonValueInsight.peakValue)} • ${wonValueInsight.peakMonth}` },
                  ] : []}
                />
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body position-relative">
                <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="Proposal lost rate by department per month" /></div>
                <h3 className="card-title mb-3">Proposal Lost Rate by Department</h3>
                <ChartInteractionHint />
                <div className={`analysis-card-shell ${isLoadingAnalysis ? 'is-loading' : ''}`}>
                  <ReactApexChart
                    options={lostRateOptions}
                    series={lostRateSeries}
                    type="line"
                    height={320}
                  />
                  <LoadingOverlay show={isLoadingAnalysis} label="Refreshing lost-rate trends..." />
                </div>
                <ChartInsightPanel
                  accentClass="analysis-chart-insight-rose"
                  kicker={lostRateInsight?.isPinned ? 'Pinned insight' : 'Peak insight'}
                  title={lostRateInsight ? `${lostRateInsight.seriesName} in ${lostRateInsight.month}` : ''}
                  summary={lostRateInsight ? `${formatPercent2(lostRateInsight.value)}% loss rate, contributing ${formatPercent2(lostRateInsight.shareAtPoint)}% of the selected month aggregate.` : ''}
                  stats={lostRateInsight ? [
                    { label: 'Selected Rate', value: `${formatPercent2(lostRateInsight.value)}%` },
                    { label: 'Month Aggregate', value: `${formatPercent2(lostRateInsight.totalAtPoint)}%` },
                    { label: 'Series Peak', value: `${formatPercent2(lostRateInsight.peakValue)}% • ${lostRateInsight.peakMonth}` },
                  ] : []}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Second row: Proposal Funnel, Lead Source, Product Win Rate, Sectors */}
        <div className="row row-deck row-cards mt-4">
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body position-relative">
                <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="Proposal stages and counts" /></div>
                <h3 className="card-title mb-3">Proposal Funnel</h3>
                <ChartInteractionHint />
                <div className={`analysis-card-shell ${isLoadingAnalysis ? 'is-loading' : ''}`}>
                  <ReactApexChart
                    options={proposalFunnelOptions}
                    series={[{ name: 'Count', data: analysisData.proposalFunnel?.data || [] }]}
                    type="bar"
                    height={320}
                  />
                  <LoadingOverlay show={isLoadingAnalysis} label="Refreshing proposal funnel..." />
                </div>
                <ChartInsightPanel
                  accentClass="analysis-chart-insight-cyan"
                  kicker={proposalFunnelInsight?.isPinned ? 'Pinned insight' : 'Largest stage'}
                  title={proposalFunnelInsight?.label || ''}
                  summary={proposalFunnelInsight ? `${proposalFunnelInsight.label} contributes ${formatPercent2(proposalFunnelInsight.share)}% of visible proposal funnel volume.` : ''}
                  stats={proposalFunnelInsight ? [
                    { label: 'Stage Count', value: proposalFunnelInsight.value },
                    { label: 'Visible Total', value: proposalFunnelInsight.total },
                    { label: 'Stage Rank', value: `#${proposalFunnelInsight.rank} of ${proposalFunnelInsight.itemCount}` },
                  ] : []}
                />
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body position-relative">
                <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="Prospects by lead source" /></div>
                <h3 className="card-title mb-3">Prospects by Lead Source</h3>
                <ChartInteractionHint />
                <div className={`analysis-card-shell ${isLoadingAnalysis ? 'is-loading' : ''}`}>
                  <ReactApexChart
                    options={leadSourceOptions}
                    series={[{ name: 'Count', data: analysisData.prospectsByLeadSource?.data || [] }]}
                    type="bar"
                    height={320}
                  />
                  <LoadingOverlay show={isLoadingAnalysis} label="Refreshing lead sources..." />
                </div>
                <ChartInsightPanel
                  accentClass="analysis-chart-insight-teal"
                  kicker={leadSourceInsight?.isPinned ? 'Pinned insight' : 'Top source'}
                  title={leadSourceInsight?.label || ''}
                  summary={leadSourceInsight ? `${leadSourceInsight.label} accounts for ${formatPercent2(leadSourceInsight.share)}% of visible prospects.` : ''}
                  stats={leadSourceInsight ? [
                    { label: 'Prospects', value: leadSourceInsight.value },
                    { label: 'Visible Total', value: leadSourceInsight.total },
                    { label: 'Source Rank', value: `#${leadSourceInsight.rank} of ${leadSourceInsight.itemCount}` },
                  ] : []}
                />
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body position-relative">
                <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="Top 5 products by win rate" /></div>
                <h3 className="card-title mb-3">Top 5 Product Win Rate (%)</h3>
                <ChartInteractionHint />
                <div className={`analysis-card-shell ${isLoadingAnalysis ? 'is-loading' : ''}`}>
                  <ReactApexChart
                    options={productWinRateOptions}
                    series={[{ name: 'Win Rate %', data: analysisData.productWinRate?.data || [] }]}
                    type="bar"
                    height={280}
                  />
                  <LoadingOverlay show={isLoadingAnalysis} label="Refreshing product win rates..." />
                </div>
                <ChartInsightPanel
                  accentClass="analysis-chart-insight-violet"
                  kicker={productWinRateInsight?.isPinned ? 'Pinned insight' : 'Highest win rate'}
                  title={productWinRateInsight?.label || ''}
                  summary={productWinRateInsight ? `${formatPercent2(productWinRateInsight.value)}% win rate with rank #${productWinRateInsight.rank}.` : ''}
                  stats={productWinRateInsight ? [
                    { label: 'Win Rate', value: `${formatPercent2(productWinRateInsight.value)}%` },
                    { label: 'Share of Top 5', value: `${formatPercent2(productWinRateInsight.share)}%` },
                    { label: 'Visible Products', value: productWinRateInsight.itemCount },
                  ] : []}
                />
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="card h-100">
              <div className="card-body position-relative d-flex flex-column">
                <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="All sectors by proposal count" /></div>
                <h3 className="card-title mb-3">Sectors (by Proposal Count)</h3>
                <div className="analysis-sector-pill">Click a sector row to apply the same filter across all Analysis sections.</div>
                <div className={`analysis-card-shell ${isLoadingAnalysis ? 'is-loading' : ''}`}>
                  <div className="analysis-sector-list table-responsive">
                    <table className="table table-vcenter card-table table-striped table-sm mb-0">
                      <thead className="sticky-top bg-white">
                        <tr>
                          <th className="text-uppercase text-muted small">Sector</th>
                          <th className="text-uppercase text-muted small text-end">Proposals</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(analysisData.topSectors || []).map((row, index) => (
                          <tr
                            key={`${row?.sector || 'sector'}-${index}`}
                            className="analysis-sector-row"
                            onClick={() => handleSectorListClick(index)}
                            title={`Apply filter for ${row?.sector || 'this sector'}`}
                          >
                            <td>
                              <span className="analysis-sector-name">{row?.sector || 'Unknown Sector'}</span>
                            </td>
                            <td className="text-end fw-semibold">{Number(row?.proposals) || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <LoadingOverlay show={isLoadingAnalysis} label="Refreshing sector distribution..." />
                </div>
                <ChartInsightPanel
                  accentClass="analysis-chart-insight-cyan"
                  kicker={sectorInsight?.isPinned ? 'Pinned insight' : 'Largest sector'}
                  title={sectorInsight?.label || ''}
                  summary={sectorInsight ? `${sectorInsight.label} contributes ${formatPercent2(sectorInsight.share)}% of visible proposal volume across sectors.` : ''}
                  stats={sectorInsight ? [
                    { label: 'Proposal Count', value: sectorInsight.value },
                    { label: 'Visible Total', value: sectorInsight.total },
                    { label: 'Sector Rank', value: `#${sectorInsight.rank} of ${sectorInsight.itemCount}` },
                  ] : []}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Third row: Top Performers, PIC Performance Analysis */}
        <div className="row row-deck row-cards mt-4">
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body position-relative">
                <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="Top performers by won value" /></div>
                <h3 className="card-title mb-3">Top Performers (by Won Value)</h3>
                <div className="table-responsive" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  <table className="table table-vcenter card-table table-striped table-sm mb-0">
                    <thead className="sticky-top bg-white">
                      <tr>
                        <th className="text-uppercase text-muted small text-nowrap">Name</th>
                        <th className="text-uppercase text-muted small text-end text-nowrap">Won</th>
                        <th className="text-uppercase text-muted small text-end text-nowrap">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(analysisData.topPerformers || []).map((row, i) => (
                        <tr key={i}>
                          <td>{row.name}</td>
                          <td className="text-end">{row.won}</td>
                          <td className="text-end">{formatCurrencyVal(row.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body position-relative">
                <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="Proposals per PIC by stage from API picPerformance." /></div>
                <h3 className="card-title mb-3">PIC Performance Analysis</h3>
                <div className="table-responsive" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  <table className="table table-vcenter card-table table-striped mb-0">
                    <thead className="sticky-top bg-white">
                      <tr>
                        <th className="small text-dark fw-bold">NAME</th>
                        <th className="text-uppercase text-muted small text-end">Total</th>
                        <th className="text-uppercase text-muted small text-end">Open</th>
                        <th className="text-uppercase text-muted small text-end">M1</th>
                        <th className="text-uppercase text-muted small text-end">M2</th>
                        <th className="text-uppercase text-muted small text-end">M3</th>
                        <th className="text-uppercase text-muted small text-end">Nego</th>
                        <th className="text-uppercase text-muted small text-end">Won</th>
                        <th className="text-uppercase text-muted small text-end">Lost</th>
                        <th className="text-uppercase text-muted small text-end">Win Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(analysisData.picPerformance || []).map((row, i) => (
                        <tr key={i}>
                          <td className="fw-bold text-dark">{row.name}</td>
                          <td className="text-end text-secondary">{row.total}</td>
                          <td className="text-end text-secondary">{row.open}</td>
                          <td className="text-end text-secondary">{row.m1}</td>
                          <td className="text-end text-secondary">{row.m2}</td>
                          <td className="text-end text-secondary">{row.m3}</td>
                          <td className="text-end text-secondary">{row.nego}</td>
                          <td className="text-end text-secondary">{row.won}</td>
                          <td className="text-end text-secondary">{row.lost}</td>
                          <td className="text-end text-secondary">{formatPercent2(row.winRate)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Competitor Analysis (full width) */}
        <div className="row row-deck row-cards mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body position-relative">
                <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="Performance against competitors" /></div>
                <h3 className="card-title mb-3">Competitor Analysis</h3>
                <div className="table-responsive">
                  <table className="table table-vcenter card-table table-striped">
                    <thead>
                      <tr>
                        <th className="text-uppercase text-muted small">Competitor</th>
                        <th className="text-uppercase text-muted small text-end">Faced</th>
                        <th className="text-uppercase text-muted small text-end">Our Wins</th>
                        <th className="text-uppercase text-muted small text-end">Our Losses</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(analysisData.competitorAnalysis || []).map((row, i) => (
                        <tr key={i}>
                          <td>{row.competitor}</td>
                          <td className="text-end">{row.faced}</td>
                          <td className="text-end">{row.ourWins}</td>
                          <td className="text-end">{row.ourLosses}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lost Proposal Analysis */}
        <div className="mt-5 pt-4 border-top">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h2 className="page-title mb-0">Lost Proposal Analysis</h2>
            <span className="text-muted d-inline-flex"><InfoTooltip text="Analysis of lost proposals by reason and competitor" /></span>
          </div>
          <div className="row row-deck row-cards mt-4">
            <div className="col-lg-6">
              <div className="card">
                <div className="card-body position-relative">
                  <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="Lost value in IDR millions by reason" /></div>
                  <h3 className="card-title mb-3">Lost Value by Reason</h3>
                  <ChartInteractionHint />
                  <div className={`analysis-card-shell ${isLoadingAnalysis ? 'is-loading' : ''}`}>
                    <ReactApexChart
                      options={lostValueByReasonOptions}
                      series={[{ name: 'Lost Value', data: analysisData.lostValueByReason?.data || [] }]}
                      type="bar"
                      height={220}
                    />
                    <LoadingOverlay show={isLoadingAnalysis} label="Refreshing lost-value reasons..." />
                  </div>
                  <ChartInsightPanel
                    accentClass="analysis-chart-insight-orange"
                    kicker={lostValueInsight?.isPinned ? 'Pinned insight' : 'Largest loss bucket'}
                    title={lostValueInsight?.label || ''}
                    summary={lostValueInsight ? `${formatCurrencyVal(lostValueInsight.value)} represents ${formatPercent2(lostValueInsight.share)}% of the visible lost value.` : ''}
                    stats={lostValueInsight ? [
                      { label: 'Lost Value', value: formatCurrencyVal(lostValueInsight.value) },
                      { label: 'Visible Total', value: formatCurrencyVal(lostValueInsight.total) },
                      { label: 'Reason Rank', value: `#${lostValueInsight.rank} of ${lostValueInsight.itemCount}` },
                    ] : []}
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card">
                <div className="card-body position-relative">
                  <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="Number of lost proposals by reason" /></div>
                  <h3 className="card-title mb-3">Lost Count by Reason</h3>
                  <ChartInteractionHint />
                  <div className={`analysis-card-shell ${isLoadingAnalysis ? 'is-loading' : ''}`}>
                    <ReactApexChart
                      options={lostCountByReasonOptions}
                      series={[{ name: 'Count', data: analysisData.lostCountByReason?.data || [] }]}
                      type="bar"
                      height={220}
                    />
                    <LoadingOverlay show={isLoadingAnalysis} label="Refreshing lost-count reasons..." />
                  </div>
                  <ChartInsightPanel
                    accentClass="analysis-chart-insight-amber"
                    kicker={lostCountInsight?.isPinned ? 'Pinned insight' : 'Largest loss count'}
                    title={lostCountInsight?.label || ''}
                    summary={lostCountInsight ? `${lostCountInsight.value} lost proposals represent ${formatPercent2(lostCountInsight.share)}% of the visible lost-count split.` : ''}
                    stats={lostCountInsight ? [
                      { label: 'Lost Count', value: lostCountInsight.value },
                      { label: 'Visible Total', value: lostCountInsight.total },
                      { label: 'Reason Rank', value: `#${lostCountInsight.rank} of ${lostCountInsight.itemCount}` },
                    ] : []}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row row-deck row-cards mt-4">
            <div className="col-12">
              <div className="card">
                <div className="card-body position-relative">
                  <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="Lost proposals by competitor" /></div>
                  <h3 className="card-title mb-3">Lost Count by Competitor</h3>
                  <div className="table-responsive">
                    <table className="table table-vcenter card-table table-striped">
                      <thead>
                        <tr>
                          <th className="text-uppercase text-muted small">Competitor</th>
                          <th className="text-uppercase text-muted small text-end">Lost Count</th>
                          <th className="text-uppercase text-muted small text-end">Total Lost Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(analysisData.lostCountByCompetitor || []).map((row, i) => (
                          <tr key={i}>
                            <td>{row.competitor}</td>
                            <td className="text-end">{row.lostCount}</td>
                          <td className="text-end">{formatCurrencyVal(row.totalLostValue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row row-deck row-cards mt-4">
            <div className="col-12">
              <div className="card">
                <div className="card-body position-relative">
                  <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="Detailed list of lost proposals" /></div>
                  <h3 className="card-title mb-3">Detailed Lost Proposals</h3>
                  <div className="analysis-table-caption">This list always follows the active analysis filters, date range, and chart drilldown context shown above.</div>
                  <GenericView
                    data={detailedLostDataWithYearTotals}
                    columns={columnsLost}
                    isLoading={isLoadingAnalysis}
                    checkboxSelection={false}
                    showColumnToggle={false}
                    tableClassName="table card-table table-vcenter text-nowrap datatable table-hover"
                    searchComponent={
                      <SearchInput
                        value={searchQueryLost}
                        onChange={setSearchQueryLost}
                        placeholder="Search lost proposals..."
                        uniqueId="analysis-lost-search"
                        autocomplete="off"
                      />
                    }
                    emptyStateMessage="No lost proposals found"
                    showTopControls={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Proposal Data (All Stages) */}
        <div className="mt-5 pt-4 border-top">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h2 className="page-title mb-0">
              Detailed Proposal Data (All Stages)
              {isLoadingAnalysis && (
                <span className="spinner-border spinner-border-sm ms-2 text-muted" role="status" />
              )}
            </h2>
            <span className="text-muted d-inline-flex"><InfoTooltip text="All proposals with amount and expense by stage" /></span>
          </div>
          <div className="row row-deck row-cards mt-4">
            <div className="col-12">
              <div className="card">
                <div className="card-body position-relative">
                  <div className="analysis-table-caption">Every proposal row below is synchronized with the same current filter context, including chart-triggered drilldowns.</div>
                  <GenericView
                    data={detailedProposalDataWithTotal}
                    columns={columnsProposal}
                    isLoading={isLoadingAnalysis}
                    checkboxSelection={false}
                    showColumnToggle={false}
                    tableClassName="table card-table table-vcenter text-nowrap datatable table-hover"
                    searchComponent={
                      <SearchInput
                        value={searchQueryProposal}
                        onChange={setSearchQueryProposal}
                        placeholder="Search proposals..."
                        uniqueId="analysis-proposal-search"
                        autocomplete="off"
                      />
                    }
                    emptyStateMessage={isLoadingAnalysis ? 'Loading proposals...' : 'No proposals found'}
                    showTopControls={true}
                    pagination={{
                      currentPage: proposalCurrentPage,
                      totalRows: detailedProposalFiltered.length,
                      rowsPerPage: proposalRowsPerPage,
                      onPageChange: setProposalCurrentPage,
                      onRowsPerPageChange: (size) => {
                        setProposalRowsPerPage(size);
                        setProposalCurrentPage(1);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
        )}

        {activeTab === 'annual-revenue-projection' && (
          <>
            {annualProjectionError && (
              <div className="alert alert-danger mb-4" role="alert">
                {annualProjectionError}
              </div>
            )}
            {/* KPI cards from GET /crm/annual-revenue-projection/analysis */}
            <div className="row row-deck row-cards mb-4">
              <div className="col-sm-6 col-lg-3">
                <KPICard
                  title="Total Proposal (Converted to Project)"
                  value={annualProjectionLoading ? '—' : annualTotalProposalWon}
                  description={`Won proposals in ${currentProjectionYear}`}
                />
              </div>
              <div className="col-sm-6 col-lg-3">
                <KPICard
                  title="Win Rate"
                  value={annualProjectionLoading ? '—' : `${formatPercent2(annualProjectionState?.kpi?.proposalWinRate)}%`}
                  description={`Win rate for proposals in ${currentProjectionYear}`}
                />
              </div>
              <div className="col-sm-6 col-lg-3">
                <KPICard
                  title="Total Revenue"
                  value={annualProjectionLoading ? '—' : formatCurrencyVal(annualProjectionState?.kpi?.totalWonValue ?? 0)}
                  description={`Revenue contribution in ${currentProjectionYear}`}
                />
              </div>
              <div className="col-sm-6 col-lg-3">
                <KPICard
                  title="Total Expenses"
                  value={annualProjectionLoading ? '—' : formatCurrencyVal(annualProjectionState?.kpi?.totalExpenses ?? 0)}
                  description={`Expenses recorded in ${currentProjectionYear}`}
                />
              </div>
            </div>

            {/* Annual Revenue Projection chart + PIC Performance Analysis */}
            <div className="row row-deck row-cards">
              <div className="col-lg-8">
                <div className="card">
                  <div className="card-body position-relative">
                    <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="5-year revenue projection by department" /></div>
                    <h3 className="card-title mb-3">
                      Annual Revenue Projection by Department
                      {projectionYears.length > 0 && (
                        <span className="text-muted fw-normal fs-6 ms-2">
                          ({projectionYears[0]}
                          {projectionYears.length > 1 ? `–${projectionYears[projectionYears.length - 1]}` : ''})
                        </span>
                      )}
                    </h3>
                    <ChartInteractionHint />
                    {annualProjectionLoading ? (
                      <div className="d-flex align-items-center justify-content-center text-muted" style={{ minHeight: 320 }}>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Loading chart…
                      </div>
                    ) : !annualRevenueProjectionSeries.length ? (
                      <div className="d-flex align-items-center justify-content-center text-muted" style={{ minHeight: 320 }}>
                        No chart data to display.
                      </div>
                    ) : (
                      <ReactApexChart
                        options={annualRevenueProjectionOptions}
                        series={annualRevenueProjectionSeries}
                        type="line"
                        height={320}
                      />
                    )}
                    {!annualProjectionLoading && annualRevenueProjectionSeries.length > 0 && (
                      <ChartInsightPanel
                        accentClass="analysis-chart-insight-sky"
                        kicker={annualRevenueInsight?.isPinned ? 'Pinned insight' : 'Peak projection'}
                        title={annualRevenueInsight ? `${annualRevenueInsight.seriesName} in ${annualRevenueInsight.month}` : ''}
                        summary={annualRevenueInsight ? `${formatCurrencyVal(annualRevenueInsight.value)} is projected, contributing ${formatPercent2(annualRevenueInsight.shareAtPoint)}% of the selected year total.` : ''}
                        stats={annualRevenueInsight ? [
                          { label: 'Projected Value', value: formatCurrencyVal(annualRevenueInsight.value) },
                          { label: 'Year Total', value: formatCurrencyVal(annualRevenueInsight.totalAtPoint) },
                          { label: 'Series Peak', value: `${formatCurrencyVal(annualRevenueInsight.peakValue)} • ${annualRevenueInsight.peakMonth}` },
                        ] : []}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card h-100">
                  <div className="card-body position-relative">
                    <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text={`PIC annual won value in ${currentProjectionYear}`} /></div>
                    <h3 className="card-title mb-3">PIC Performance Analysis ({currentProjectionYear})</h3>
                    <div className="table-responsive" style={{ maxHeight: 'min(70vh, 560px)', overflowY: 'auto' }}>
                      {annualProjectionLoading ? (
                        <div className="text-muted py-5 text-center">
                          <span className="spinner-border spinner-border-sm me-2" role="status" />
                          Loading…
                        </div>
                      ) : (
                        <table className="table table-vcenter card-table table-striped mb-0">
                          <thead className="sticky-top bg-white">
                            <tr>
                              <th className="text-uppercase text-muted small">Name</th>
                              <th className="text-uppercase text-muted small text-end">Annual Won Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(annualProjectionState?.topPerformers || []).map((row, i) => (
                              <tr key={i}>
                                <td>{row.name}</td>
                                <td className="text-end">{row.value != null ? formatCurrencyVal(row.value) : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Annual Breakdown */}
            <div className="row row-deck row-cards mt-4">
              <div className="col-12">
                <div className="card">
                  <div className="card-body position-relative">
                    <div className="position-absolute top-0 end-0 p-2 text-muted d-inline-flex"><InfoTooltip text="Annual value breakdown by proposal and year" /></div>
                    <h3 className="card-title mb-3">Detailed Annual Breakdown</h3>
                    <GenericView
                      data={breakdownDataWithTotals}
                      columns={columnsBreakdown}
                      checkboxSelection={false}
                      showColumnToggle={false}
                      tableClassName="table card-table table-vcenter text-nowrap datatable table-hover"
                      emptyStateMessage={annualProjectionLoading ? 'Loading breakdown…' : 'No breakdown data available'}
                      showTopControls={true}
                      pagination={{
                        currentPage: breakdownApiPage,
                        totalRows: annualProjectionState?.detailedAnnualBreakdownPaginated?.totalItems ?? 0,
                        rowsPerPage: breakdownApiPageSize,
                        onPageChange: setBreakdownApiPage,
                        onRowsPerPageChange: (size) => {
                          setBreakdownApiPageSize(size);
                          setBreakdownApiPage(1);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Filter Offcanvas */}
        <div className="offcanvas offcanvas-end" tabIndex="-1" id="analysisFilterOffcanvas" aria-labelledby="analysisFilterOffcanvasLabel">
          <div className="offcanvas-header">
            <h2 className="offcanvas-title" id="analysisFilterOffcanvasLabel">Filter</h2>
            <button type="button" className="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
          </div>
          <div className="offcanvas-body">
            <div className="mb-3">
              <label className="form-label">Select Period</label>
              <AppSelect
                name="period"
                value={filters.period === 'all' ? '' : (filters.period || '')}
                onChange={(name, value) => handleFilterChange(name, value || 'all')}
                options={PERIOD_OPTIONS.filter((opt) => opt.id !== 'all')}
                placeholder="All Time"
                valueField="id"
                labelField="name"
                label=""
                sortField={{ field: '$order' }}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Currency</label>
              <AppSelect
                name="currency"
                value={currency}
                onChange={(name, value) => setCurrency(value)}
                options={[
                  { id: '', name: 'All Currencies' },
                  ...(currenciesList || []).map((c) => ({
                    id: (c.currencyCode || String(c.id || '')).toString(),
                    name: `${(c.currencyCode || '').toString().toUpperCase()} — ${c.currencyName || c.name || ''}`
                  }))
                ]}
                placeholder="All Currencies"
                valueField="id"
                labelField="name"
                label=""
                sortField={{ field: 'name' }}
              />
              <div className="form-text">When selected, only proposals with this base currency will be shown.</div>
            </div>
            {filters.period === 'custom' && (
              <div className="mb-3">
                <label className="form-label">Date Range</label>
                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label small">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={customDateRange.startDate}
                      onChange={(e) => setCustomDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={customDateRange.endDate}
                      min={customDateRange.startDate}
                      onChange={(e) => setCustomDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            )}

            {showDepartmentFilter && (
            <div className="mb-3">
              <label className="form-label">Department</label>
              <AppSelect
                name="department"
                value={Array.isArray(filters.department) ? filters.department : []}
                onChange={handleFilterChange}
                options={[{ id: 'all', name: 'All' }, ...businessUnitsData]}
                placeholder="All"
                valueField="id"
                labelField="name"
                label=""
                sortField={{ field: '$order' }}
                multiple
              />
            </div>
            )}

            {showSalesCreatorFilter && (
            <div className="mb-3">
              <label className="form-label">Sales Creator</label>
              <AppSelect
                name="salesCreator"
                value={Array.isArray(filters.salesCreator) ? filters.salesCreator : []}
                onChange={handleFilterChange}
                options={[{ id: 'all', name: 'All' }, ...cascadedSalesCreatorOptions]}
                placeholder="All"
                valueField="id"
                labelField="name"
                label=""
                sortField={{ field: '$order' }}
                multiple
              />
            </div>
            )}

            <div className="mb-3">
              <label className="form-label">Branch</label>
              <AppSelect
                name="branchId"
                value={filters.branchId == null || filters.branchId === '' || filters.branchId === 'all' ? '' : filters.branchId}
                onChange={(name, value) => handleFilterChange(name, value || '')}
                options={[{ id: '', name: 'All Branch' }, ...branchesData]}
                placeholder="All Branch"
                valueField="id"
                labelField="name"
                label=""
                sortField={{ field: '$order' }}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Tagging</label>
              <AppSelect
                name="taggingIds"
                value={Array.isArray(filters.taggingIds) ? filters.taggingIds : []}
                onChange={handleFilterChange}
                options={[{ id: 'all', name: 'All Tagging' }, ...taggingOptionsData]}
                placeholder="All Tagging"
                valueField="id"
                labelField="name"
                label=""
                sortField={{ field: '$order' }}
                multiple
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Sector</label>
              <AppSelect
                name="sector"
                value={Array.isArray(filters.sector) ? filters.sector : []}
                onChange={handleFilterChange}
                options={[{ id: 'all', name: 'All Sector' }, ...sectorsData]}
                placeholder="All Sector"
                valueField="id"
                labelField="name"
                label=""
                sortField={{ field: '$order' }}
                multiple
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Lead Source</label>
              <AppSelect
                name="leadSource"
                value={Array.isArray(filters.leadSource) ? filters.leadSource : []}
                onChange={handleFilterChange}
                options={[{ id: 'all', name: 'All Source' }, ...sourcesData]}
                placeholder="All Source"
                valueField="id"
                labelField="name"
                label=""
                sortField={{ field: '$order' }}
                multiple
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Standard</label>
              <AppSelect
                name="standard"
                value={Array.isArray(filters.standard) ? filters.standard : []}
                onChange={handleFilterChange}
                options={[{ id: 'all', name: 'All Standard' }, ...standardsData]}
                placeholder="All Standard"
                valueField="id"
                labelField="name"
                label=""
                sortField={{ field: '$order' }}
                multiple
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Proposal Stage</label>
              <AppSelect
                name="proposalStage"
                value={Array.isArray(filters.proposalStage) ? filters.proposalStage : []}
                onChange={handleFilterChange}
                options={[{ id: 'all', name: 'All' }, ...proposalStagesData]}
                placeholder="All"
                valueField="id"
                labelField="name"
                label=""
                sortField={{ field: '$order' }}
                multiple
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Proposal Approval Status</label>
              <AppSelect
                name="proposalApprovalStatus"
                value={filters.proposalApprovalStatus === 'all' ? '' : (filters.proposalApprovalStatus || '')}
                onChange={(name, value) => handleFilterChange(name, value || 'all')}
                options={PROPOSAL_APPROVAL_STATUS_OPTIONS.filter((opt) => opt.id !== 'all')}
                placeholder="All"
                valueField="id"
                labelField="name"
                label=""
                sortField={{ field: '$order' }}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Competitor</label>
              <AppSelect
                name="competitor"
                value={Array.isArray(filters.competitor) ? filters.competitor : []}
                onChange={handleFilterChange}
                options={[{ id: 'all', name: 'All' }, ...competitorOptions]}
                placeholder="All"
                valueField="id"
                labelField="name"
                label=""
                sortField={{ field: '$order' }}
                multiple
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Lost Reason</label>
              <AppSelect
                name="lostReason"
                value={Array.isArray(filters.lostReason) ? filters.lostReason : []}
                onChange={handleFilterChange}
                options={[{ id: 'all', name: 'All' }, ...lostReasonOptions]}
                placeholder="All"
                valueField="id"
                labelField="name"
                label=""
                sortField={{ field: '$order' }}
                multiple
              />
            </div>

            <div className="d-flex gap-2 mt-4">
              <button type="button" className="btn btn-outline-primary flex-fill" onClick={handleResetFilter}>
                Reset
              </button>
              <button type="button" className="btn btn-primary flex-fill" onClick={handleApplyFilter}>
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analysis;
